import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LanguageCode } from '../helper-types/general/consts/language-code';

i18n.use(initReactI18next).init({
    resources: {
        en: {
            translation: {
                generic: {
                    confirmationDialog: {
                        title: 'Confirm Deletion',
                        description: 'Are you sure you want to delete this item? This action cannot be undone',
                        confirmButton: 'Delete',
                        cancelButton: 'Cancel'
                    }
                },
                pwa: {
                    updateAvailable: 'Update available',
                    updateMessage: 'A new version of the app is available. Would you like to update now?',
                },
                nav: {
                    sections: {
                        myLearning: 'My Learning',
                        content: 'Content',
                        liveAndAttendance: 'Live & Attendance',
                        people: 'People',
                        operations: 'Operations',
                        settings: 'Settings',
                    },
                    dashboard: 'Dashboard',
                    myCourses: 'Courses',
                    myExams: 'Exams',
                    myLiveRooms: 'My Online Rooms',
                    myProfile: 'My Profile',
                    myProfileShort: 'Profile',
                    subjects: 'Subjects',
                    courses: 'Courses',
                    questionsBanks: 'Questions Banks',
                    questions: 'Questions',
                    exams: 'Exams',
                    pricingPlans: 'Pricing Plans',
                    licenses: 'Licenses',
                    enrollmentRequests: 'Enrollment Requests',
                    profile: 'Profile',
                    tenantBranding: 'Branding',
                    tenantIntegrations: 'Integrations',
                    tenantPaymentChannels: 'Payment Channels',
                    users: 'Employees',
                    students: 'Students',
                    studentResults: 'Student Results',
                    liveRooms: 'Online Rooms',
                    attendance: 'Attendance',
                    financialReports: 'Financial Reports',
                    roles: 'Jobs',
                    more: 'More',
                    rooms: 'Rooms',
                    home: 'Home',
                    attendanceShort: 'Attendance'
                },
                auth: {
                    login: {
                        welcome: 'Welcome Back to Your Platform',
                        title: 'Login',
                        email: 'Email or username',
                        emailPlaceholder: 'Enter your username or email',
                        password: 'Password',
                        loginButton: 'Log In',
                        rememberMe: 'Remember me',
                        errors: {
                            loginError: 'An error occurred while logging in. Please try again',
                            emailRequired: 'Email is required',
                        },
                        google: {
                            noAccount: "We couldn't find an account for this Google email. Register first to create one.",
                        },
                        selectTenant: {
                            title: 'Select Platform',
                            subtitle: 'You are enrolled in multiple platforms. Please select one to continue',
                            searchPlaceholder: 'Search platforms...',
                            noResults: 'No platforms match your search',
                            backToLogin: 'Back to Login',
                        },
                        noAccountPrompt: "Don't have a student account?",
                        registerNow: 'Register as a student',
                    },
                    register: {
                        title: 'Create Your Student Account',
                        subtitle: 'Register to start learning with {{name}}',
                        fallbackTenantName: 'your platform',
                        submitButton: 'Register',
                        successTitle: 'Registration Successful',
                        successMessage: 'Your account has been created. You can now log in.',
                        goToLogin: 'Go to Login',
                        invalidLinkTitle: 'Invalid Registration Link',
                        invalidLinkMessage: 'This registration link is missing or invalid. Please go back and use the registration link from your platform’s page',
                        backToLogin: 'Back to Login',
                        confirmPassword: 'Confirm Password',
                        passwordsDoNotMatch: 'Passwords do not match',
                        whatsAppNumber: 'Your WhatsApp Number',
                        usernamePlaceholder: 'user_name',
                        gradeSearchPlaceholder: 'Search grade...',
                        noGrades: 'No matching grades',
                        google: {
                            completeTitle: 'Almost done, {{name}}',
                            completeSubtitle: "We just need your phone number and grade to finish setting up your account",
                            existingAccountTitle: 'Welcome back',
                            existingAccountMessage: 'You already have an account with several centres — please continue on the login page.',
                        },
                    },
                    signOut: 'Sign Out',
                    signIn: 'Sign In',
                },
                dashboard: {
                    systemName: 'Itqan Platform',
                    welcome: 'Welcome to {{name}}',
                    noData: 'No data available',
                    sections: {
                        contentStatistics: 'Content Statistics',
                        userStatistics: 'Employee Statistics',
                        licenseStatistics: 'License Statistics',
                        examPerformance: 'Exam Performance',
                        revenueStatistics: 'Revenue Statistics',
                        courseProgress: 'Course Progress',
                        recentActivity: 'Recent Activity (Last 30 Days)',
                        studentEngagement: 'Student Engagement',
                        usageLimits: 'Usage & Limits',
                    },
                    usage: {
                        students: 'Students',
                        courses: 'Courses',
                        exams: 'Exams',
                        storage: 'Storage',
                        aiTokens: 'AI Usage (Monthly)',
                        aiTokensUnit: 'coin',
                        unlimited: 'Unlimited',
                    },
                    statistics: {
                        subjects: 'Subjects',
                        courses: 'Courses',
                        questionBanks: 'Question Banks',
                        questions: 'Questions',
                        exams: 'Exams',
                        chapters: 'Chapters',
                        courseItems: 'Course Items',
                        totalStudents: 'Total Students',
                        activeStudents: 'Active Students',
                        totalUsers: 'Total Employees',
                        activeUsers: 'Active Employees',
                        totalLicenses: 'Total Licenses',
                        activeLicenses: 'Active Licenses',
                        expiredLicenses: 'Expired Licenses',
                        revokedLicenses: 'Revoked Licenses',
                        totalExamAttempts: 'Total Exam Attempts',
                        passedExamAttempts: 'Passed Attempts',
                        failedExamAttempts: 'Failed Attempts',
                        revenueThisMonth: 'Revenue This Month (Licenses + Paid Live Rooms)',
                        revenueThisYear: 'Revenue This Year (Licenses + Paid Live Rooms)',
                        newLicensesThisMonth: 'New Licenses This Month',
                        newLicensesThisYear: 'New Licenses This Year',
                        enrolledCourses: 'Enrolled Courses',
                        completedItems: 'Completed Items',
                        totalItems: 'Total Items',
                        overallProgress: 'Overall Progress',
                        itemsCompleted: 'items completed',
                        examAttempts: 'Exam Attempts',
                        passedExams: 'Passed Exams',
                        failedExams: 'Failed Exams',
                        averageScore: 'Average Score',
                        excellent: 'Excellent!',
                        topPerformer: 'Top Performer!',
                        activeCourseLicenses: 'Active Course Licenses',
                        activeExamLicenses: 'Active Exam Licenses',
                        recentlyAddedCourses: 'Recently Added Courses',
                        recentlyAddedQuestions: 'Recently Added Questions',
                        recentlyAddedExams: 'Recently Added Exams',
                        activeStudentsToday: 'Active Students Today',
                    }
                },
                subject: {
                    subjectsTree: 'Subjects Tree',
                    subjects: 'Subjects',
                    subjectsDescription: 'Main entry point for all subjects',
                    addSubjectDescription: 'Create a new subject for your curriculum',
                    updateSubjectDescription: 'Modify subject details',
                    addChapterDescription: 'Add a new chapter to the subject',
                    updateChapterDescription: 'Modify chapter details',
                    addSubject: 'Add Subject',
                    addChapter: 'Add Chapter',
                    updateSubject: 'Update Subject',
                    updateChapter: 'Update Chapter',
                    deleteSubject: 'Delete Subject',
                    deleteChapter: 'Delete Chapter',
                    title: 'Title',
                    description: 'Description',
                    grade: 'Grade',
                    deleteSubjectConfirmTitle: 'Confirm Subject Deletion',
                    deleteSubjectConfirmDescription: 'Are you sure you want to delete the subject "{{name}}"? This action cannot be undone',
                    deleteChapterConfirmTitle: 'Confirm Chapter Deletion',
                    deleteChapterConfirmDescription: 'Are you sure you want to delete the chapter "{{name}}"? This action cannot be undone',
                    gradeLabels: {
                        general: 'General',
                        primaryFirst: 'Primary 1st',
                        primarySecond: 'Primary 2nd',
                        primaryThird: 'Primary 3rd',
                        primaryFourth: 'Primary 4th',
                        primaryFifth: 'Primary 5th',
                        primarySixth: 'Primary 6th',
                        preparatoryFirst: 'Preparatory 1st',
                        preparatorySecond: 'Preparatory 2nd',
                        preparatoryThird: 'Preparatory 3rd',
                        secondaryFirst: 'Secondary 1st (or equivalent)',
                        secondarySecond: 'Secondary 2nd (or equivalent)',
                        secondaryThird: 'Secondary 3rd (or equivalent)',
                        universityFirst: 'University 1st',
                        universitySecond: 'University 2nd',
                        universityThird: 'University 3rd',
                        universityFourth: 'University 4th',
                        universityFifth: 'University 5th',
                    },
                    errors: {
                        titleRequired: 'Title is required',
                        titleMaxLength: 'Title must be at most {{max}} characters long',
                        descriptionRequired: 'Description is required',
                        descriptionMaxLength: 'Description must be at most {{max}} characters long',
                        gradeAllowedValues: 'Grade must be one of the allowed values',
                    }
                },
                pricingPlan: {
                    gridTitle: 'Pricing Plans',
                    addPricingPlan: 'Add Pricing Plan',
                    title: 'Title',
                    description: 'Description',
                    price: 'Price',
                    timeUnit: 'Time Unit',
                    currency: 'Currency',
                    grade: 'Grade',
                    isActive: 'Is Active?',
                    isPopular: 'Mark as Most Popular',
                    editPricingPlan: 'Update Pricing Plan',
                    deletePricingPlan: 'Delete Pricing Plan',
                    deletePricingPlanConfirmTitle: 'Confirm Pricing Plan Deletion',
                    deletePricingPlanConfirmDescription: 'Are you sure you want to delete the pricing plan "{{title}}"? This action cannot be undone',
                    active: 'Active',
                    inactive: 'Inactive',
                    timeUnits: {
                        month: 'Month',
                        term: 'Term',
                        fullAcademicYear: 'Full Academic Year',
                    },
                    gridDescription: 'Manage and organize your subscription pricing plans',
                    popular: 'Popular',
                    levels: {
                        general: 'General',
                        primary: 'Primary',
                        preparatory: 'Preparatory',
                        secondary: 'Secondary',
                        university: 'University',
                    },
                    errors: {
                        titleRequired: 'Title is required',
                        titleMaxLength: 'Title must be at most {{max}} characters long',
                        descriptionRequired: 'Description is required',
                        descriptionMaxLength: 'Description must be at most {{max}} characters long',
                        priceMinValue: 'Price must be at least {{min}}',
                        priceMaxValue: 'Price must be at most {{max}}',
                        currencyAllowedValues: 'Currency must be one of the allowed values',
                        gradeAllowedValues: 'Grade must be one of the allowed values',
                        timeUnitAllowedValues: 'Time unit must be one of the allowed values',
                        noPlansAdded: 'No pricing plans yet',
                        noPlansAddedSubtitle: 'Create your first pricing plan to get started',
                    }
                },
                enrollment: {
                    coursesCatalogTitle: 'Courses',
                    coursesCatalogSubtitle: 'Browse courses available for your grade and enroll or request access',
                    examsCatalogTitle: 'Exams',
                    examsCatalogSubtitle: 'Browse exams available for your grade and enroll or request access',
                    states: {
                        notEnrolled: 'Not Enrolled',
                        pending: 'Pending',
                        enrolled: 'Enrolled',
                    },
                    free: 'Free',
                    priceFormat: '{{price}} {{currency}}',
                    enrollNow: 'Enroll Now',
                    requestEnrollment: 'Request',
                    cancelRequest: 'Cancel Request',
                    goToMyCourses: 'Go to My Courses',
                    goToMyExams: 'Go to My Exams',
                    cancelRequestConfirmTitle: 'Cancel Enrollment Request',
                    cancelRequestConfirmDescription: 'Are you sure you want to cancel your enrollment request for "{{title}}"?',
                    noCoursesAvailable: 'No courses available',
                    noCoursesAvailableSubtitle: 'There are no courses available for your grade yet',
                    noExamsAvailable: 'No exams available',
                    noExamsAvailableSubtitle: 'There are no exams available for your grade yet',
                    requestDialog: {
                        title: 'Request Enrollment',
                        paymentProofLabel: 'Payment proof (optional)',
                        uploadPaymentProof: 'Upload Payment Proof',
                        confirm: 'Send Request',
                        totalFormula: '{{count}} × {{unitPrice}} {{currency}} =',
                        paymentChannelsLabel: 'Payment method used',
                        noPaymentChannels: 'The instructor has not set up any payment channels yet — contact them directly to arrange payment.',
                    },
                },
                enrollmentRequest: {
                    pageTitle: 'Enrollment Requests',
                    pageDescription: 'Review and respond to student enrollment requests',
                    tableTitle: 'Requests',
                    status: 'Status',
                    student: 'Student',
                    grade: 'Grade',
                    requestedAt: 'Requested At',
                    requested: 'Requested',
                    monthsCount: '{{count}} Months',
                    attemptsCountShort: '{{count}} Attempts',
                    paymentProof: 'Payment Proof',
                    viewPaymentProof: 'View Payment Proof',
                    noRequestsFound: 'No enrollment requests found',
                    approve: 'Approve',
                    reject: 'Reject',
                    forStudent: 'Requested by {{name}}',
                    paidVia: 'Paid via {{channel}}',
                    statusLabels: {
                        pending: 'Pending',
                        approved: 'Approved',
                        rejected: 'Rejected',
                        cancelledByStudent: 'Cancelled by Student',
                    },
                    aiVerdictLabel: 'AI check',
                    aiAutoApproved: 'Auto-approved by AI',
                    aiVerdict: {
                        match: 'Match',
                        amountMismatch: 'Amount mismatch',
                        channelMismatch: 'Channel mismatch',
                        unreadable: 'Unreadable image',
                        uncertain: 'Uncertain',
                        duplicateProof: 'Duplicate image',
                        rateLimited: 'Rate limited',
                    },
                    approveDialog: {
                        title: 'Approve Enrollment Request',
                        accessMonthsCount: 'Access Duration (months)',
                        attemptsCount: 'Attempts Count',
                    },
                    rejectDialog: {
                        title: 'Reject Enrollment Request',
                        noteLabel: 'Reason (optional)',
                    },
                },
                studentExam: {
                    pageTitle: 'Exams',
                    pageDescription: 'Your exams — enrolled ones and the full catalog to browse',
                    enterExam: 'Enter Exam',
                    enrolledTab: 'Enrolled',
                    browseTab: 'Browse All',
                    noExamsAvailable: 'No exams yet',
                    noExamsAvailableSubtitle: "You're not enrolled in any exams yet — browse what's available below.",
                    browseExamsButton: 'Browse Exams',
                    questionsCount: '{{count}} Questions',
                    timeLimit: '{{minutes}} mins',
                    timeLimitUnlimited: 'Unlimited',
                    totalScore: 'Total: {{score}} pts',
                    passingScore: 'Pass: {{score}} pts',
                    previousAttempt: 'Previous Score: {{score}}/{{total}}',
                    submitExam: 'Submit Exam',
                    passed: 'Passed',
                    failed: 'Failed',
                    correctAnswersCount: '{{correct}} of {{total}} correct',
                    question: 'Question',
                    points: 'Points',
                    hint: 'Hint',
                    correct: 'Correct',
                    incorrect: 'Incorrect',
                    pointsEarned: '{{earned}}/{{max}} points',
                    progress: '{{answered}} of {{total}} questions answered',
                    previous: 'Previous',
                    next: 'Next',
                    noQuestions: 'This exam has no questions',
                    backToExams: 'Back to Exams',
                    questionNavigation: 'Question Navigation',
                    legendAnswered: 'Answered',
                    legendUnanswered: 'Unanswered',
                    legendFlagged: 'Flagged for review',
                    flagForReview: 'Flag for review',
                    confirmSubmitTitle: 'Confirm Submission',
                    confirmSubmitMessage: 'Are you sure you want to submit your exam? You cannot change your answers after submission',
                    unansweredWarning: 'You have {{count}} unanswered question(s)',
                    timeUpAutoSubmit: 'Time is up! Your exam has been automatically submitted',
                    fullscreen: 'Enter fullscreen',
                    exitFullscreen: 'Exit fullscreen',
                    attemptsInfo: '{{used}} / {{max}} attempts used',
                    noAttemptsLeft: 'No attempts left',
                    viewPastResults: 'View Results',
                    pastResultsTitle: 'Past Attempts — {{examTitle}}',
                    attemptNumber: 'Attempt #{{number}}',
                    questionOf: 'Question {{current}} of {{total}}',
                    answeredCount: '{{count}} Answered',
                    unansweredCount: '{{count}} Unanswered',
                    flaggedCount: '{{count}} Flagged',
                    reviewAnswers: 'Review Answers',
                    yourScore: 'Your Score',
                    congratulations: 'Congratulations! You Passed!',
                    betterLuckNextTime: 'You did not pass this time',
                    correctCount: '{{correct}}/{{total}} Correct',
                    passingScoreRequired: 'Pass: {{score}} pts',
                    summary: 'Summary',
                    examInfo: 'Exam Info',
                    examinee: 'Examinee',
                    totalPointsLabel: '{{points}} pts total',
                    timeLimitLabel: '{{minutes}} min',
                    noTimeLimitLabel: 'No time limit',
                    filterAll: 'All',
                    filterUnanswered: 'Unanswered',
                    filterFlagged: 'Flagged',
                    increaseFontSize: 'Increase font size',
                    decreaseFontSize: 'Decrease font size',
                    eliminateOption: 'Right-click to eliminate',
                    eliminateOptionMobile: 'Long-press to eliminate',
                    optionEliminated: 'Eliminated',
                    shortcuts: 'Shortcuts',
                    shortcutNavigate: 'Navigate',
                    shortcutFlag: 'Flag question',
                    shortcutJump: 'Jump to Q#',
                    timeSpent: 'Time: {{time}}',
                    timeSpentSeconds: '{{seconds}}s',
                    timeSpentMinutes: '{{minutes}}m {{seconds}}s',
                    typeMCSA: 'Single Answer',
                    typeMCMA: 'Multiple Answers',
                    typeTOrF: 'True or False',
                    typeEssay: 'Essay',
                    typeMentalMathSheet: 'Mental Math Sheet',
                    essayAnswerPlaceholder: 'Type your answer here...',
                    mentalMathAnswerPlaceholder: 'Answer',
                    mentalMathCorrectAnswer: 'Correct answer:',
                    aiGradingUnavailable: 'Automatic grading was unavailable — recorded with a score of 0',
                    aiGradedFeedback: 'Feedback',
                    examGateRulesTitle: 'Exam Rules',
                    examGateRule1: 'The exam runs in fullscreen — do not exit until you submit',
                    examGateRule2: 'Any attempt to leave or disrupt the exam is monitored and recorded',
                    examGateStart: 'Start Exam (Fullscreen)',
                    examGateContinue: 'Continue Exam (Fullscreen)',
                    progressRestored: 'Your previous progress has been restored',
                    fullscreenLostTitle: 'You Left the Exam Screen',
                    fullscreenLostMessage: 'Fullscreen mode was exited. Click the button below to return to your exam',
                    fullscreenLostReturn: 'Return to Exam',
                    violationsCount: '{{count}} violation(s)',
                    tabSwitchWarning: 'Warning: Tab switching was detected and recorded as a violation',
                },
                studentCourse: {
                    pageTitle: 'Courses',
                    pageDescription: 'Your courses — enrolled ones and the full catalog to browse',
                    startCourse: 'Start Course',
                    continueCourse: 'Continue Course',
                    enrolledTab: 'Enrolled',
                    browseTab: 'Browse All',
                    noCoursesEnrolled: 'No courses yet',
                    noCoursesEnrolledSubtitle: "You're not enrolled in any courses yet — browse what's available below.",
                    browseCoursesButton: 'Browse Courses',
                    accessPeriod: 'Available From {{from}} To {{to}}',
                    courseProgress: 'Course Progress',
                    itemsCompleted: '{{completed}} of {{total}} items completed',
                    backToMyCourses: 'Back to My Courses',
                    watchVideo: 'Watch Video',
                    videoComplete: 'Video complete',
                    replayVideo: 'Replay',
                    videoQuality: 'Quality',
                    videoQualityAuto: 'Auto',
                    viewMaterial: 'View Material',
                    startQuiz: 'Start Quiz',
                    questions: 'Questions',
                    score: 'Score',
                    section: 'Section',
                    courseOverview: 'Course Overview',
                    selectItem: 'Select an item from the sidebar to begin',
                    totalItems: 'Total Items',
                    completedItems: 'Completed',
                    openMaterial: 'Open Material',
                    materialDescription: 'Download or view this learning material',
                    quizInfo: 'Quiz - {{count}} Questions',
                    previous: 'Previous',
                    next: 'Next',
                    showSidebar: 'Course Items',
                    quiz: {
                        question: 'Question',
                        points: 'Points',
                        hint: 'Hint',
                        correct: 'Correct',
                        incorrect: 'Incorrect',
                        pointsEarned: '{{earned}}/{{max}} points',
                        mentalMathAnswerPlaceholder: 'Answer',
                        mentalMathCorrectAnswer: 'Correct answer:',
                        progress: '{{answered}} of {{total}} questions answered',
                        resultsTitle: 'Quiz Results',
                        scoreResult: 'Your Score: {{earned}}/{{total}} points',
                        correctAnswers: 'Correct Answers: {{correct}}/{{total}}',
                        previous: 'Previous',
                        next: 'Next',
                        submitQuiz: 'Submit Quiz',
                        confirmSubmitTitle: 'Submit Quiz',
                        confirmSubmitDescription: 'Are you sure you want to submit the quiz? You will not be able to change your answers',
                        noQuestions: 'This quiz has no questions',
                        quizCompleted: 'Quiz completed!',
                        quizTimeLimitDisplay: '{{minutes}} min',
                        quizTimeUpAutoSubmit: 'Time is up! Your quiz has been automatically submitted',
                    },
                },
                course: {
                    gridTitle: 'Courses',
                    gridDescription: 'Manage and organize your educational courses',
                    addCourseDescription: 'Create a new educational course',
                    editCourseDescription: 'Update course details',
                    active: 'Active',
                    inactive: 'Inactive',
                    month: 'month',
                    free: 'Free',
                    coursesCount: 'Courses',
                    videosShort: 'Videos',
                    quizzesShort: 'Quizzes',
                    materialsShort: 'Materials',
                    noResources: 'No resources',
                    videos: '{{count}} Videos',
                    quizzes: '{{count}} Quizzes',
                    materials: '{{count}} Materials',
                    academicYear: 'Academic Year',
                    difficultyLevel: 'Difficulty Level',
                    accessType: 'Access Type',
                    durationFormat: 'Hours',
                    editCourse: 'Update Course',
                    deleteCourse: 'Delete Course',
                    deleteCourseConfirmTitle: 'Confirm Course Deletion',
                    deleteCourseConfirmDescription: 'Are you sure you want to delete the course "{{title}}"? This action cannot be undone',
                    addCourse: 'Add Course',
                    objective: 'Objective',
                    title: 'Title',
                    subject: 'Subject',
                    durationInHours: 'Duration In Hours',
                    subscriptionPerMonth: 'Subscription / Month',
                    currency: 'Currency',
                    isActive: 'Is Active?',
                    thumbnail: 'Cover Image',
                    language: 'Course Language',
                    languageArabic: 'Arabic',
                    languageEnglish: 'English',
                    difficultyLevelLabels: {
                        everyone: 'Everyone',
                        beginner: 'Beginner',
                        intermediate: 'Intermediate',
                        advanced: 'Advanced',
                    },
                    itemTypeLabels: {
                        video: 'Video',
                        quiz: 'Quiz',
                        material: 'Material',
                    },
                    resourceAccessTypeLabels: {
                        open: 'Open',
                        requiresLicense: 'Requires License',
                    },
                    courseItem: {
                        courseItems: 'Course Items',
                        courseItemsDescription: 'Manage and organize your course content',
                        addCourseItems: 'Add Course Items',
                        addCourseItem: 'Add Course Item',
                        updateCourseItem: 'Update Course Item',
                        title: "Title",
                        objective: "Objective",
                        itemType: "Item Type",
                        pointsPerQuestion: "Grade Per Question",
                        quizTimeLimit: "Quiz Time Limit (minutes)",
                        quizTimeLimitHelper: "Leave empty for no time limit",
                        lockedForEditing: 'This quiz has student submissions. Only the title, objective, and divider can be changed',
                        question: "Question",
                        questions: "Questions",
                        video: "Video",
                        material: "Material",
                        youtubeSource: "YouTube",
                        uploadSource: "Upload",
                        hasDivider: "Has Divider (As An End Of Section)",
                        endOfSection: "End Of Section",
                        editCourseItem: "Edit Course Item",
                        preview: {
                            previewCourseItem: "Preview Course Item",
                            previewCourseItemDescription: "Preview how this item appears to students",
                            openMaterial: "Open Material",
                        },
                        deleteCourseItem: "Delete Course Item",
                        deleteCourseItemConfirmTitle: "Confirm Course Item Deletion",
                        deleteCourseItemConfirmDescription: 'Are you sure you want to delete the course item "{{title}}"? This action cannot be undone',
                        addCourseItemDescription: 'Add a new item to your course',
                        updateCourseItemDescription: 'Update course item details',
                        errors: {
                            titleRequired: 'Title is required',
                            titleMaxLength: 'Title must be at most {{max}} characters long',
                            objectiveRequired: 'Objective is required',
                            objectiveMaxLength: 'Objective must be at most {{max}} characters long',
                            itemTypeAllowedValues: 'Item type must be one of the allowed values',
                            resourceTypeMismatch: 'Selected resource type must match the item type',
                            videoResourceUriRequired: 'You should select a YouTube video',
                            videoUploadRequired: 'You should upload a video',
                            videoUploadInProgress: 'Please wait until the video upload finishes',
                            pointsPerQuestionPositive: 'Grade per question must be a positive number',
                            questionsIdsRequired: 'At least one question should be selected',
                            timeLimitNonnegative: 'Time limit must be zero or a positive number',
                            materialResourceRelativeUriRequired: 'You should upload a file',
                            noCourseItems: 'No course items added yet',
                            noCourseItemsSubtitle: 'Start by adding your first course item',
                        }
                    },
                    errors: {
                        titleRequired: 'Title is required',
                        titleMaxLength: 'Title must be at most {{max}} characters long',
                        objectiveRequired: 'Objective is required',
                        objectiveMaxLength: 'Objective must be at most {{max}} characters long',
                        subjectIdValue: 'Subject is required',
                        durationValue: 'Duration must be a number greater than zero',
                        subscriptionValue: 'Subscription must be greater than zero when access type is "Requires License"',
                        academicYearRequired: 'Academic year is required',
                        academicYearMaxLength: 'Academic year must be at most {{max}} characters long',
                        difficultyLevelAllowedValues: 'Difficulty level must be one of the allowed values',
                        accessTypeAllowedValues: 'Access type must be one of the allowed values',
                        currencyAllowedValues: 'Currency must be one of the allowed values',
                        noCoursesAdded: 'No courses added yet',
                        noCoursesAddedSubtitle: 'Add courses to display here',
                        courseNotLoaded: 'Course could not be loaded',
                    }
                },
                question: {
                    tableTitle: 'Questions',
                    questionsDescription: 'Create and manage your assessment questions',
                    addQuestionDescription: 'Create a new assessment question',
                    editQuestionDescription: 'Update the assessment question details',
                    code: 'Code',
                    text: 'Text',
                    type: 'Type',
                    difficultyLevel: 'Difficulty Level',
                    hint: 'Hint',
                    modelAnswer: 'Model Answer',
                    modelAnswerHelp: 'The reference/correct answer. AI compares the meaning of the student\'s answer against this when auto-grading (optional)',
                    essayNoOptionsNote: 'Students answer with free text. This question is graded automatically by AI.',
                    mentalMathSheetNote: 'Build a sheet of columns — each column is a chain of numbers joined by a single operator. The correct answer for each column is always computed by the platform, never entered manually.',
                    mentalMathColumns: 'Columns',
                    mentalMathColumn: 'Column {{number}}',
                    operator: 'Operator',
                    operand: 'Number {{number}}',
                    addOperand: 'Add Number',
                    removeOperand: 'Remove Number',
                    addColumn: 'Add Column',
                    removeColumn: 'Remove Column',
                    correctAnswerPreview: 'Answer:',
                    invalidColumnPreview: 'Answer: —',
                    arithmeticOperatorLabels: {
                        add: 'Add (+)',
                        subtract: 'Subtract (−)',
                        multiply: 'Multiply (×)',
                        divide: 'Divide (÷)',
                    },
                    optionsRandomized: 'Allow Options Randomization In Exams',
                    options: 'Options',
                    option: 'Option',
                    isCorrect: 'Is Correct',
                    removeOption: 'Remove Option',
                    addOption: 'Add Option',
                    addQuestion: 'Add Question',
                    questionsBank: 'Questions Bank',
                    viewQuestion: 'View Question',
                    viewQuestionDescription: 'View question details',
                    editQuestion: 'Edit Question',
                    deleteQuestion: 'Delete Question',
                    aiGenerated: 'Generated by AI',
                    manualSource: 'Manually authored',
                    source: 'Source',
                    deleteQuestionConfirmTitle: 'Confirm Question Deletion',
                    deleteQuestionConfirmDescription: 'Are you sure you want to delete the question "{{text}}"? This action cannot be undone',
                    difficultyLevelLabels: {
                        easy: 'Easy',
                        medium: 'Medium',
                        hard: 'Hard',
                        veryHard: 'Very Hard',
                    },
                    typeLabels: {
                        mcsa: 'Multiple Choice - Single Answer',
                        mcma: 'Multiple Choice - Multiple Answers',
                        tOrF: 'True or False',
                        essay: 'Essay (AI-Graded)',
                        mentalMathSheet: 'Mental Math Sheet',
                    },
                    trueFalseOptions: {
                        true: 'True',
                        false: 'False',
                    },
                    errors: {
                        textRequired: 'Text is required',
                        typeAllowedValues: 'Type must be one of the allowed values',
                        difficultyLevelAllowedValues: 'Difficulty level must be one of the allowed values',
                        questionsBankRequired: 'Questions bank is required',
                        hintMaxLength: 'Hint must be at most {{max}} characters long',
                        modelAnswerMaxLength: 'Model answer must be at most {{max}} characters long',
                        noQuestionsFound: 'No questions found',
                        minimumTwoOptions: 'Minimum two options are required',
                        trueFalseOptionsInvalid: 'Select one of the True or False options, as the right answer',
                        exactlyOneCorrectOption: 'Select exactly one correct option',
                        atLeastOneCorrectOption: 'Select, at least, one correct option',
                        essayHasNoOptions: 'Essay questions cannot have options',
                        mentalMathColumnsRequired: 'A Mental Math Sheet question requires at least one column',
                        minimumTwoOperands: 'A column requires at least two numbers',
                        operatorsCountMismatch: 'A column\'s operators do not match its numbers',
                        singleOperatorOnly: 'A column must use a single operator only',
                        mentalMathDivideByZero: 'A column cannot divide by zero',
                    },
                },
                exam: {
                    tableTitle: 'Exams',
                    examsDescription: 'Create, manage and monitor your examinations',
                    addExamDescription: 'Create a new examination',
                    editExamDescription: 'Update examination details',
                    lockedForEditing: 'This exam has student attempts. Only the title, description, active status, exam language, and max attempts (free access) can be changed',
                    title: 'Title',
                    description: 'Description',
                    subjectTitle: 'Subject',
                    chapterTitle: 'Chapter',
                    questionsCount: 'Questions Count',
                    questionsCountHelper: 'Auto-calculated from selected questions',
                    timeLimit: 'Time Limit (in mins.)',
                    totalScore: 'Total Score',
                    subscriptionPerAttempt: 'Subscription Per Attempt',
                    currency: 'Currency',
                    accessType: 'Access Type',
                    maxFreeAttempts: 'Max Attempts (Free Access)',
                    maxFreeAttemptsHelper: 'Leave empty for unlimited attempts per student',
                    grade: 'Grade',
                    createdAt: 'Created At',
                    isActive: 'Is Active',
                    thumbnail: 'Cover Image',
                    addExam: 'Add Exam',
                    subject: 'Subject',
                    chapter: 'Chapter',
                    editExam: 'Update Exam',
                    viewExam: 'View Exam',
                    viewExamDescription: 'Preview the exam questions, order and answers',
                    deleteExam: 'Delete Exam',
                    deleteExamConfirmTitle: 'Confirm Exam Deletion',
                    deleteExamConfirmDescription: 'Are you sure you want to delete the exam "{{title}}"? This action cannot be undone',
                    formTimeLimit: 'Time Limit In Mins. (0 means no limit)',
                    passingScore: 'Passing Score',
                    randomizeQuestions: 'Randomize Questions',
                    language: 'Exam Language',
                    languageArabic: 'Arabic',
                    languageEnglish: 'English',
                    selectQuestions: 'Select Questions',
                    selectedQuestions: 'Selected Questions',
                    questionScore: 'Question Score',
                    questionNumber: 'Question Number',
                    dragToReorder: 'Drag to reorder',
                    fetchedQuestions: 'Fetched Questions',
                    filtrationPanel: 'Filtration Panel',
                    timeLimitUnlimited: 'Unlimited',
                    copyExam: 'Copy Exam',
                    copyExamConfirmTitle: 'Confirm Exam Copying',
                    copyExamConfirmDescription: 'Are you sure you want to copy the exam "{{title}}"? This action cannot be undone',
                    exportResults: 'Export Results',
                    exportResultsDialog: {
                        title: 'Export Results — {{title}}',
                        description: 'Download a CSV of all attempts at this exam, one row per attempt.',
                        academicYear: 'Academic Year',
                        allAcademicYears: 'All academic years',
                        exportButton: 'Export',
                        exporting: 'Exporting...',
                        exportError: 'Failed to export results. Please try again',
                    },
                    randomizeQuestionsTableHeader: 'Randomize Questions',
                    questionSelectionMethodLabels: {
                        manual: 'Manual',
                        automatic: 'Automatic',
                        upload: 'From Exam File',
                    },
                    autoGenerate: {
                        title: 'Auto-Generate Questions',
                        questionsCount: 'Questions Count',
                        generateButton: 'Generate',
                        requirementsHint: 'Select at least one questions bank, question type, and difficulty level to generate questions',
                        successMessage: '{{count}} questions generated successfully',
                        partialResultsWarning: 'Only {{generated}} of the {{requested}} requested questions were found matching your criteria',
                    },
                    upload: {
                        chooseFile: 'Choose exam file',
                        fileHelp: 'Accepted: images (JPG/PNG/WEBP), up to 15 MB.',
                        notes: 'Notes for the AI (optional)',
                        notesHelp: 'Any extra context, e.g. "ignore question 5, it\'s incomplete"',
                        extract: 'Extract Questions',
                        extracting: 'Extracting…',
                        reviewHeading: '{{count}} questions found — review, edit, and select which to include',
                        selectedCount: '{{count}} selected',
                        errors: {
                            extractionFailed: 'Failed to extract questions from the uploaded file',
                        },
                    },
                    errors: {
                        noExamsFound: 'No exams found',
                        titleRequired: 'Title is required',
                        titleMaxLength: 'Title must be at most {{max}} characters long',
                        descriptionRequired: 'Description is required',
                        descriptionMaxLength: 'Description must be at most {{max}} characters long',
                        questionsCountMin: 'Questions count must be at least 1',
                        timeLimitMin: 'Time limit must be at least 0',
                        totalScoreMin: 'Total score must be at least 1',
                        passingScoreMin: 'Passing score must be at least 1',
                        subscriptionPerAttemptMin: 'Subscription per attempt must be at least 0',
                        currencyAllowedValues: 'Currency must be one of the allowed values',
                        accessTypeAllowedValues: 'Access type must be one of the allowed values',
                        maxFreeAttemptsMin: 'Max attempts must be at least 1',
                        subjectRequired: 'Subject is required',
                        passingScoreExceedsTotalScore: 'Passing score must be less than or equal to total score',
                        noQuestionsSelected: 'No selected questions found',
                        questionsRequired: 'Questions are required',
                        selectedQuestionsCountNotMatch: 'Selected questions count must be equal to specified questions count',
                        questionScoreMin: 'Each question score must be at least 1',
                        questionScoreNotMatch: 'Total questions scores must be equal to specified total score',
                        subscriptionPerAttemptValueInCaseRequiresLicense: 'Subscription per attempt must be greater than zero, in case access type is "Requires License"',
                        scoreMin: 'Score must be at least 1',
                        difficultyLevelAllowedValues: 'Difficulty level must be one of the allowed values',
                        questionTypeAllowedValues: 'Question type must be one of the allowed values',
                        fileHashRequired: 'File hash is required',
                        fileNameRequired: 'File name is required',
                    }
                },
                sunEditor: {
                    insertNewEquation: 'Insert New Equation',
                    cancelInsertion: 'Cancel Insertion',
                    cancelUpdate: 'Cancel Update',
                    uploadImage: 'Image',
                    uploadVideo: 'Video',
                    uploadAudio: 'Audio',
                    uploading: 'Uploading...',
                    uploadingPercent: 'Uploading... {{percent}}%',
                    equationsEditor: {
                        placeholder: '\\text{Enter an equation}',
                        insertEquation: 'Insert Equation',
                        updateEquation: 'Update Equation',
                        cardTitle: 'Equation Editor',
                        previewHeadline: 'Equation Preview',
                        renderEnglish: 'Display in English',
                        renderArabic: 'Display in Arabic',
                    },
                    errors: {
                        failedToCaptureScreenshot: 'Failed to capture equation: {{error}}',
                        uploadFailed: 'Failed to upload file',
                        fileTooLarge: 'File is too large. Maximum allowed size is {{maxMb}} MB',
                    }
                },
                fileUploader: {
                    fileUploader: 'File Uploader',
                    dropOrClick: 'Click or drag a file here to upload',
                    chooseFile: 'Choose File',
                    selectedFile: 'Selected File',
                    uploadFile: 'Upload File',
                    uploading: 'Uploading...',
                    uploadingPercent: 'Uploading... {{percent}}%',
                    viewFile: 'View uploaded file',
                    errors: {
                        noFileSelected: 'No file selected',
                    }
                },
                videoUploader: {
                    chooseFile: 'Choose Video',
                    uploadFile: 'Upload Video',
                    uploading: 'Uploading...',
                    preview: 'Preview',
                },
                youTubeVideoSelector: {
                    title: 'YouTube Video Selector',
                    browseChannel: 'Browse Channel',
                    pasteLink: 'Paste Link',
                    pasteLinkDescription: 'Paste a YouTube video URL or ID to select it directly. This also works with unlisted videos',
                    fetch: 'Search',
                    views: 'Views',
                    duration: 'Duration',
                    previous: 'Previous',
                    next: 'Next',
                    page: 'Page',
                    refreshVideos: 'Refresh Videos',
                    urlPlaceholder: 'https://youtube.com/watch?v=... or video ID',
                    errors: {
                        noVideosFound: 'No videos found',
                        invalidUrl: 'Invalid YouTube URL or video ID',
                        videoNotFound: 'Video not found',
                    }
                },
                questionsBank: {
                    tableTitle: 'Questions Banks',
                    questionsBankDescription: 'Manage your question repositories and knowledge banks',
                    addQuestionsBankDescription: 'Create a new questions bank repository',
                    editQuestionsBankDescription: 'Update questions bank details',
                    title: 'Title',
                    description: 'Description',
                    subjectTitle: 'Subject',
                    chapterTitle: 'Chapter',
                    isActive: 'Is Active?',
                    addQuestionsBank: 'Add Questions Bank',
                    editQuestionsBank: 'Update Questions Bank',
                    viewQuestionsBank: 'View Questions Bank',
                    viewQuestionsBankDescription: 'Browse this bank\'s questions',
                    deleteQuestionsBank: 'Delete Questions Bank',
                    deleteQuestionsBankConfirmTitle: 'Confirm Delete Questions Bank',
                    deleteQuestionsBankConfirmDescription: 'Are you sure you want to delete the questions bank "{{title}}"? This action cannot be undone',
                    subject: 'Subject',
                    chapter: 'Chapter',
                    questionsCount: 'Questions Count',
                    errors: {
                        titleRequired: 'Title is required',
                        titleMaxLength: 'Title must be at most {{max}} characters long',
                        descriptionRequired: 'Description is required',
                        descriptionMaxLength: 'Description must be at most {{max}} characters long',
                        subjectRequired: 'Subject is required',
                        noQuestionsBanksFound: 'No questions banks found',
                    }
                },
                license: {
                    tableTitle: 'Licenses',
                    gridDescription: 'Manage and control access licenses for your students',
                    grantLicenseDescription: 'Grant resource access licenses to students',
                    resourceType: 'Resource Type',
                    resourceTitle: 'Resource Title',
                    totalSubscriptionFee: 'Total Subscription Fee',
                    totalFee: 'Total Fee',
                    studentCount: 'Students',
                    mixedStatus: 'Mixed',
                    startDate: 'Start Date',
                    endDate: 'End Date',
                    attemptsCount: 'Attempts Count',
                    status: 'Status',
                    createdAt: 'Created At',
                    grantLicense: 'Grant License',
                    deleteLicense: 'Delete License',
                    deleteLicenseConfirmTitle: 'Confirm Delete License',
                    deleteLicenseConfirmDescription: 'Are you sure you want to delete the license of the resource "{{title}}"? This action cannot be undone',
                    selectStudents: 'Select Students',
                    accessMonthsCount: 'License Months Count',
                    chooseGrade: 'Choose Grade',
                    currency: 'Currency',
                    changeLicenseStatusConfirmTitle: 'Confirm Change License Status',
                    changeLicenseStatusConfirmDescription: 'Are you sure you want to change the status of the license to be "{{status}}"? This action cannot be undone',
                    changeLicenseStatusToPausedOrActive: 'Activate / Pause License',
                    changeLicenseStatusToRevoked: 'Revoke License',
                    viewStudents: 'View Students',
                    licenseStudents: 'License Students',
                    revokeLicense: 'Revoke License',
                    revokeLicenseConfirmTitle: 'Confirm Revoke License',
                    revokeLicenseConfirmDescription: 'Are you sure you want to revoke the license of the user "{{name}}"? This action cannot be undone',
                    resourceTypeLabels: {
                        course: 'Course',
                        exam: 'Exam',
                    },
                    licenseStatusLabels: {
                        active: 'Active',
                        paused: 'Paused',
                        expired: 'Expired',
                        revoked: 'Revoked',
                    },
                    errors: {
                        licenseStudentsRequired: 'License students are required',
                        resourceTypeAllowedValues: 'Resource type must be one of the allowed values',
                        accessMonthsCountPositive: 'Access months count must be greater than zero',
                        attemptsCountPositive: 'Attempts count must be greater than zero',
                        licenseStatusAllowedValues: 'License status must be one of the allowed values',
                        noLicensesFound: 'No licenses found',
                        resourceRequired: 'Resource is required',
                        noStudentsFound: 'No students found'
                    }
                },
                user: {
                    tableTitle: 'Employees',
                    usersGridDescription: 'Manage employee accounts, jobs, and access permissions',
                    addUserDescription: 'Create a new employee account',
                    editUserDescription: 'Update employee details',
                    changeOtherPasswordDescription: 'Change the password for another employee account',
                    addUser: 'Add Employee',
                    editUser: 'Edit Employee',
                    deleteUser: 'Delete Employee',
                    firstName: 'First Name',
                    lastName: 'Last Name',
                    email: 'Email',
                    password: 'Password',
                    phoneNumber: 'Phone Number',
                    createdAt: 'Created At',
                    isActive: 'Is Active',
                    roles: 'Jobs',
                    withRole: 'With Job',
                    deleteUserConfirmTitle: 'Confirm Employee Deletion',
                    deleteUserConfirmDescription: 'Are you sure you want to delete the employee "{{name}}"? This action cannot be undone',
                    editProfile: 'Edit Profile',
                    profileDescription: 'Manage your personal information and account security',
                    profileData: 'Profile Data',
                    changePassword: 'Change Password',
                    changePasswordDescription: 'Update your account password securely',
                    currentPassword: 'Current Password',
                    newPassword: 'New Password',
                    phoneNumberHelperText: 'e.g., 01512345678',
                    emailImportantNote: 'Invariant once registered',
                    changeOtherPassword: 'Change Other Password',
                    confirmingPassword: 'Confirm Password',
                    errors: {
                        firstNameRequired: 'First name is required',
                        firstNameMaxLength: 'First name must be at most {{max}} characters long',
                        lastNameRequired: 'Last name is required',
                        lastNameMaxLength: 'Last name must be at most {{max}} characters long',
                        emailRequired: 'Email is required',
                        invalidEmail: 'Email is invalid',
                        passwordRequired: 'Password is required',
                        confirmingPasswordRequired: 'Confirming password is required',
                        invalidPassword: 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one digit, and one special character',
                        invalidConfirmingPassword: 'Confirming password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one digit, and one special character',
                        phoneNumberRequired: 'Phone number is required',
                        invalidEgyptianPhoneNumber: 'Phone number must be a valid Egyptian phone number',
                        invalidGrade: 'Grade must be one of the allowed values',
                        noRolesAvailable: 'No jobs available',
                        rolesNotEmpty: 'Jobs is required',
                        noUsersFound: 'No employees found',
                        passwordsDoNotMatch: 'New password and confirming password do not match',
                    }
                },
                role: {
                    tableTitle: 'Jobs',
                    rolesGridDescription: 'Manage jobs, permissions, and access control policies',
                    addRoleDescription: 'Define a new job with permissions',
                    editRoleDescription: 'Modify job details and permissions',
                    name: 'Name',
                    isActive: 'Is Active',
                    createdAt: 'Created At',
                    addRole: 'Add Job',
                    noRolesFound: 'No jobs found',
                    editRole: 'Edit Job',
                    deleteRole: 'Delete Job',
                    deleteRoleConfirmTitle: 'Confirm Job Deletion',
                    deleteRoleConfirmDescription: 'Are you sure you want to delete the job "{{name}}"? This action cannot be undone',
                    claimValues: 'Claim Values',
                    roleLabels: {
                        administrator: 'Administrator',
                        student: 'Student',
                    },
                    claims: {
                        accessSubjectsTab: 'Access Subjects Tab',
                        accessCoursesTab: 'Access Courses Tab',
                        accessQuestionsTab: 'Access Questions Tab',
                        accessQuestionsBanksTab: 'Access Questions Banks Tab',
                        accessExamsTab: 'Access Exams Tab',
                        accessPricingPlansTab: 'Access Pricing Plans Tab',
                        accessLicensesTab: 'Access Licenses Tab',
                        accessUsersTab: 'Access Employees Tab',
                        accessRolesTab: 'Access Jobs Tab',
                        accessStudentsTab: 'Access Students Tab',
                        accessStudentResultsTab: 'Access Student Results Tab',
                        accessLiveRoomsTab: 'Access Online Rooms Tab',
                        accessAttendanceTab: 'Access Attendance Tab',
                        accessFinancialReportsTab: 'Access Financial Reports Tab',
                        accessEnrollmentRequestsTab: 'Access Enrollment Requests Tab',
                        accessTenantSettingsTab: 'Access Tenant Settings Tab',
                        sendParentNotifications: 'Send Parent Notifications (WhatsApp)',
                    },
                    errors: {
                        nameRequired: 'Name is required',
                        nameMaxLength: 'Name must be at most {{max}} characters long',
                        claimsNotEmpty: 'Claims cannot be empty',
                    }
                },
                student: {
                    tableTitle: 'Students',
                    studentsGridDescription: 'Manage student accounts, grades, and academic progress',
                    addStudentDescription: 'Register a new student account',
                    editStudentDescription: 'Update student details',
                    changePasswordDescription: 'Change the password for a student account',
                    firstName: 'First Name',
                    lastName: 'Last Name',
                    email: 'Email',
                    password: 'Password',
                    whatsAppNumber: 'WhatsApp Number',
                    parentPhoneNumber: 'Parent WhatsApp Number',
                    grade: 'Grade',
                    createdAt: 'Created At',
                    isActive: 'Is Active',
                    addStudent: 'Add Student',
                    editStudent: 'Edit Student',
                    deleteStudent: 'Delete Student',
                    changePassword: 'Change Password',
                    showId: 'Show ID',
                    viewAttendanceHistory: 'Attendance History',
                    deleteStudentConfirmTitle: 'Confirm Student Deletion',
                    deleteStudentConfirmDescription: 'Are you sure you want to delete the student "{{name}}"? This action cannot be undone',
                    myProfile: {
                        pageTitle: 'My Profile',
                        pageDescription: 'View and update your personal information',
                        formTitle: 'Profile Data',
                        gradeReadOnlyHint: 'Contact an administrator to change your grade',
                    },
                    errors: {
                        noStudentsFound: 'No students found',
                    }
                },
                attendance: {
                    pageTitle: 'Attendance',
                    pageDescription: 'Take attendance by scanning student IDs or marking manually',
                    manualTab: 'Manual',
                    scanTab: 'Scan',
                    exportTab: 'Export Report',
                    searchByEmail: 'Student Email',
                    searchByEmailPlaceholder: 'Enter the student\'s email',
                    search: 'Search',
                    markPresent: 'Present',
                    markAbsent: 'Absent',
                    academicYear: 'Academic Year',
                    export: 'Export Report',
                    exportDate: 'Date (optional)',
                    scanInstruction: 'Point the camera at a student\'s QR code or barcode',
                    scanLaptopCameraHint: 'Using a laptop\'s built-in camera? It may struggle to focus at close range — try holding the code farther away, or use an external/phone camera for best results',
                    cameraError: 'Could not access the camera',
                    cameraPermissionDenied: 'Camera permission denied',
                    status: {
                        present: 'Present',
                        absent: 'Absent',
                    },
                    checkInMethod: {
                        manual: 'Manual',
                        qrCode: 'QR Code',
                        barcode: 'Barcode',
                    },
                    studentIdDialog: {
                        title: 'Student ID',
                        description: 'Show this code for attendance check-in',
                        qrTab: 'QR Code',
                        barcodeTab: 'Barcode',
                        download: 'Download',
                    },
                    history: {
                        title: 'Attendance History',
                        noHistory: 'No attendance records yet',
                        session: 'Session',
                        date: 'Date',
                        status: 'Status',
                        method: 'Method',
                        time: 'Time',
                    },
                },
                financialReports: {
                    pageTitle: 'Financial Reports',
                    pageDescription: 'Fees recorded and marked paid by admins — not confirmed payment-gateway transactions, since this platform has no payment processor yet',
                    summary: {
                        licenseFees: 'License Fees Recorded',
                        liveRoomFees: 'Live Room Fees Recorded',
                        paid: 'Paid',
                        unpaid: 'Unpaid',
                        thisMonth: 'This Month',
                        thisYear: 'This Year',
                        untilNow: 'To Date',
                    },
                    table: {
                        title: 'Charges',
                        date: 'Date',
                        type: 'Type',
                        resource: 'Resource',
                        student: 'Student',
                        amount: 'Amount',
                        status: 'Status',
                        noChargesFound: 'No charges found',
                        noChargesHint: 'Charges appear here once licenses are granted or live room sessions are billed. Try widening the date range or clearing the filters, if any.',
                    },
                    filters: {
                        startDate: 'From',
                        endDate: 'To',
                        chargeType: 'Type',
                        paidStatus: 'Paid Status',
                    },
                    chargeTypeLabels: {
                        license: 'License',
                        liveRoomSession: 'Live Room Session',
                    },
                    paidStatusLabels: {
                        paid: 'Paid',
                        unpaid: 'Unpaid',
                    },
                },
                tenantSettings: {
                    branding: {
                        pageTitle: 'Branding',
                        pageDescription: 'Manage your platform\'s name, description, logo, and colors',
                        nameAr: 'Name (Arabic)',
                        nameEn: 'Name (English)',
                        subdomain: 'Subdomain',
                        subdomainCannotChange: 'Invariant once registered',
                        description: 'Description',
                        logoUrl: 'Logo URL',
                        primaryColor: 'Primary Color',
                        secondaryColor: 'Secondary Color',
                    },
                    integrations: {
                        pageTitle: 'Integrations',
                        pageDescription: 'Connect your YouTube channel and social media links',
                        youtubeSettings: 'YouTube Settings',
                        apiKey: 'YouTube API Key',
                        apiKeyHelper: 'Optional — overrides the platform\'s default YouTube API key',
                        channelId: 'YouTube Channel ID',
                        channelIdHelper: 'Used to list your channel\'s videos as course content',
                        socialLinks: 'Social Links',
                        facebook: 'Facebook',
                        instagram: 'Instagram',
                        x: 'X (Twitter)',
                        socialYoutube: 'YouTube Channel Link',
                        whatsAppNotifications: 'WhatsApp Notifications',
                        whatsAppNotificationsHint: 'Choose which reports are sent to parents automatically. Any report can still be sent manually regardless of these settings.',
                        autoSendExamResults: 'Automatically send exam results',
                        autoSendCourseProgress: 'Automatically send course progress',
                        autoSendAttendance: 'Automatically send attendance summaries',
                    },
                    paymentChannels: {
                        pageTitle: 'Payment Channels',
                        pageDescription: 'Tell students how to pay you directly — no gateway, no fees taken by the platform',
                        intro: 'List every way you accept payment (InstaPay, Vodafone Cash, bank transfer, Fawry, cash, or anything else). Students will see this list, with the exact amount owed, before they pay you and upload their receipt.',
                        empty: 'No payment channels added yet. Add at least one so students know how to pay you.',
                        type: 'Channel Type',
                        label: 'Display Label',
                        identifier: 'Payment Details',
                        identifierPlaceholder: 'e.g. 01012345678, or IBAN / account number',
                        identifierHelper: 'The phone number, account number, or code students will send money to',
                        instructions: 'Extra Instructions (optional)',
                        instructionsPlaceholder: 'e.g. "Please write your name in the transfer note"',
                        addChannel: 'Add Payment Channel',
                        removeChannel: 'Remove',
                        newChannel: 'New Channel',
                        deleteConfirmTitle: 'Remove Payment Channel',
                        deleteConfirmDescription: 'Are you sure you want to remove "{{label}}"? Students will no longer see it as a way to pay you.',
                        types: {
                            InstaPay: 'InstaPay',
                            VodafoneCash: 'Vodafone Cash',
                            OrangeCash: 'Orange Cash',
                            EtisalatCash: 'Etisalat Cash',
                            BankTransfer: 'Bank Transfer',
                            Fawry: 'Fawry',
                            Cash: 'Cash',
                            Other: 'Other',
                        },
                        errors: {
                            typeRequired: 'Channel type is required',
                            labelRequired: 'Display label is required',
                            identifierRequired: 'Payment details are required',
                        },
                    },
                    errors: {
                        nameArRequired: 'Arabic name is required',
                        nameArMaxLength: 'Arabic name must be at most {{max}} characters long',
                        nameEnRequired: 'English name is required',
                        nameEnMaxLength: 'English name must be at most {{max}} characters long',
                        descriptionMaxLength: 'Description must be at most {{max}} characters long',
                        logoUrlMaxLength: 'Logo URL must be at most {{max}} characters long',
                        invalidHexColor: 'Must be a valid hex color (e.g., #2563EB)',
                    },
                },
                liveRoom: {
                    pageTitle: 'Online Rooms',
                    pageDescription: 'Create rooms, assign students, and toggle who can join',
                    tableTitle: 'Online Rooms',
                    createNew: 'Create Room',
                    columns: {
                        title: 'Title',
                        grade: 'Grade',
                        assignedStudents: 'Assigned Students',
                        attendees: 'Today\'s Attendees',
                        opened: 'Opened',
                    },
                    openedLabels: {
                        opened: 'Opened',
                        closed: 'Closed',
                    },
                    actions: {
                        edit: 'Edit',
                        delete: 'Delete',
                        join: 'Join',
                        manageStudentAssignments: 'Assign / Unassign Students',
                        viewAttendance: 'View Attendance',
                    },
                    fields: {
                        title: 'Title',
                        description: 'Description',
                        grade: 'Grade',
                        opened: 'Opened',
                        costPerSession: 'Cost Per Session',
                        costPerSessionHelper: 'Cost per session, considering every student takes a single session per day, regardless of his joining time. 0 = free',
                        currency: 'Currency',
                    },
                    errors: {
                        titleRequired: 'Title is required',
                        titleMaxLength: 'Title must be at most {{max}} characters',
                        descriptionRequired: 'Description is required',
                        descriptionMaxLength: 'Description must be at most {{max}} characters',
                        gradeRequired: 'Grade is required',
                        costPerSessionInvalid: 'Cost per session must be 0 or greater',
                        currencyRequired: 'Currency is required',
                        noRoomsFound: 'No online rooms found',
                        noStudentsFound: 'No students found',
                        noAttendance: 'No attendance recorded yet',
                        accessTokenError: 'Failed to get access token. Please try again',
                    },
                    dialog: {
                        createTitle: 'Create Online Room',
                        editTitle: 'Edit Online Room',
                        createButton: 'Create',
                        deleteConfirmTitle: 'Delete Online Room',
                        deleteConfirmDescription: 'Are you sure you want to delete "{{title}}"? This action cannot be undone',
                        attendanceTitle: 'Attendance Roster',
                        manageStudentAssignmentsTitle: 'Assign / Unassign Students',
                        manageStudentAssignmentsDescription: 'Only students from the room\'s grade are listed. Selection is preserved across pages',
                        searchStudents: 'Search by first name…',
                        assignedSelectedCount: '{{count}} student(s) selected',
                    },
                    attendance: {
                        studentName: 'Student Name',
                        email: 'Email',
                        firstJoinedAt: 'First Joined',
                        lastLeftAt: 'Last Left',
                        duration: 'Total Duration',
                        cost: 'Cost',
                        paid: 'Paid',
                        markPaid: 'Mark as paid',
                        markUnpaid: 'Mark as unpaid',
                        filterDate: 'Date',
                    },
                    room: {
                        hostHeader: 'You are hosting this room',
                        participantHeader: 'Online room in progress',
                        leave: 'Leave',
                        controls: {
                            mic: 'Microphone',
                            camera: 'Camera',
                            screenShare: 'Screen share',
                            muteMic: 'Mute microphone',
                            unmuteMic: 'Unmute microphone',
                            startCamera: 'Turn camera on',
                            stopCamera: 'Turn camera off',
                            shareScreen: 'Share screen',
                            stopShare: 'Stop sharing',
                            fullscreen: 'Enter fullscreen',
                            exitFullscreen: 'Exit fullscreen',
                        },
                    },
                    student: {
                        pageTitle: 'My Online Rooms',
                        pageDescription: 'Rooms you have been assigned to',
                        emptyAssigned: 'You have not been assigned to any rooms yet',
                        joinNow: 'Join Now',
                        inactiveLabel: 'Closed — cannot join right now',
                    },
                },
                studentResults: {
                    pageTitle: 'Student Results',
                    pageDescription: 'Review students performance across exams and courses',
                    searchPlaceholder: 'Search by name or email…',
                    tabs: {
                        all: 'All',
                        secondaryFirst: '1st Secondary (or equivalent)',
                        secondarySecond: '2nd Secondary (or equivalent)',
                        secondaryThird: '3rd Secondary (or equivalent)',
                    },
                    columns: {
                        name: 'Name',
                        email: 'Email',
                        grade: 'Grade',
                        examsPassedTotal: 'Exams (passed/total)',
                        avgExamScore: 'Avg Exam Score %',
                        coursesProgress: 'Course Items (finished/total)',
                        lastActive: 'Last Active',
                    },
                    cells: {
                        passedOfTotal: '{{passed}}/{{total}}',
                        finishedOfTotal: '{{finished}}/{{total}}',
                    },
                    actions: {
                        viewDetails: 'View Details',
                    },
                    errors: {
                        noStudentsFound: 'No students found',
                    },
                    detail: {
                        title: 'Student Result Details',
                        enrolledAt: 'Enrolled At',
                        close: 'Close',
                        sendReportToParent: 'Send report to parent (WhatsApp)',
                        noParentContact: 'This student has no parent contact on file',
                        loadingError: 'Failed to load student details. Please try again',
                        tabs: {
                            exams: 'Exams',
                            courses: 'Courses',
                        },
                        exams: {
                            attemptNumber: 'Attempt #{{number}}',
                            score: '{{earned}}/{{max}} ({{percent}}%)',
                            scoreLabel: 'Score',
                            passed: 'Passed',
                            failed: 'Failed',
                            inProgress: 'In progress',
                            started: 'Started: {{date}}',
                            completed: 'Completed: {{date}}',
                            noAttempts: 'No exam attempts yet',
                            question: 'Q{{number}}',
                            yourAnswer: 'Your Answer',
                            scoreOutOf: '{{earned}}/{{max}}',
                            mentalMathBlank: 'Blank',
                            mentalMathCorrectAnswer: 'Correct answer',
                        },
                        courses: {
                            noCourses: 'No course progress yet',
                            progress: '{{finished}}/{{total}} items',
                        },
                    },
                },
                notifications: {
                    title: 'Notifications',
                    empty: 'No notifications yet',
                    markAllRead: 'Mark all read',
                    typeLabels: {
                        licenseGranted: 'License Granted',
                        licenseExpiringSoon: 'License Expiring Soon',
                        courseContentPublished: 'New Content Added',
                        attendanceMarkedAbsent: 'Marked Absent',
                    },
                },
                common: {
                    switchLanguage: 'Switch Language',
                    lightMode: 'Light Mode',
                    darkMode: 'Dark Mode',
                    or: 'or',
                    displaySettings: 'Display Settings',
                    appearance: 'Appearance',
                    appVersion: 'v{{version}}',
                    loading: 'Loading...',
                    backToMain: 'Back to Main Page',
                    cancel: 'Cancel',
                    confirm: 'Confirm',
                    save: 'Save',
                    retry: 'Retry',
                    submit: 'Submit',
                    submitting: 'Submitting...',
                    delete: 'Delete',
                    active: 'Active',
                    inactive: 'Inactive',
                    actions: 'Actions',
                    filterPanel: 'Filter Panel',
                    applyFilters: 'Apply Filters',
                    clearFilters: 'Clear Filters',
                    all: 'All',
                    none: 'None',
                    optional: '(optional)',
                    yes: 'Yes',
                    no: 'No',
                    later: 'Later',
                    updateNow: 'Update Now',
                    copy: 'Copy',
                    close: 'Close',
                    admin: 'Administrator',
                    student: 'Student',
                    user: 'Employee',
                    pagination: {
                        rowsPerPage: 'Rows per page',
                        displayedRows: 'Rows {{from}}-{{to}} of {{count}}',
                    },
                    notFound: {
                        title: 'Page Not Found',
                        description: 'Sorry, the page you are looking for does not exist',
                    },
                    unauthorized: {
                        title: 'Unauthorized',
                        description: 'Sorry, the page you are looking for, you are not authorized to access it',
                    },
                    errors: {
                        networkError: 'An error occurred while connecting to the server',
                    },
                },
                ai: {
                    chat: {
                        title: 'AI Assistant',
                        subtitle: 'Alpha — instructor tools only',
                        openTooltip: 'AI assistant',
                        inputPlaceholder: 'Type your question for the assistant…',
                        reset: 'Reset conversation',
                        emptyState: 'How can I help you today?',
                        emptyHint: 'Try: "What subjects do we have?" or "Show third-secondary exams"',
                        capabilitiesTitle: 'What I can help with',
                        errors: {
                            unknown: 'The assistant ran into an unexpected error',
                        },
                    },
                    generate: {
                        openButton: 'Generate Questions with AI',
                        title: 'Generate Questions',
                        targetBankNotice: 'Suggestions will be saved into: {{bankTitle}}',
                        sourceText: 'Source material',
                        sourceHelp: 'Paste a chapter, lesson notes, or a passage and the AI will suggest questions from it',
                        numberOfQuestions: 'Number of questions',
                        preferredType: 'Preferred type',
                        preferredDifficulty: 'Preferred difficulty',
                        mixed: 'Mixed',
                        notes: 'Instructions for the AI (optional)',
                        notesHelp: 'e.g. "focus on photosynthesis", "avoid trick questions"',
                        generate: 'Generate',
                        generating: 'Generating…',
                        suggestionsHeading: '{{count}} suggestion(s) ready for review',
                        saveSelected: 'Save {{count}} question(s) to the bank',
                        saving: 'Saving…',
                        close: 'Close',
                        correct: 'Correct answer',
                        savedCount: 'Saved {{count}} question(s) successfully',
                        errors: {
                            sourceRequired: 'Source material is required',
                            generationFailed: 'Could not generate questions. Try reducing the source or rephrasing',
                            partialSaveFailure: 'Failed to save {{count}} question(s)',
                        },
                    },
                },
            }
        },
        ar: {
            translation: {
                generic: {
                    confirmationDialog: {
                        title: 'تأكيد الحذف',
                        description: 'هل أنت متأكد من أنك تريد حذف هذا العنصر؟ لا يمكن التراجع عن هذا الإجراء',
                        confirmButton: 'حذف',
                        cancelButton: 'إلغاء'
                    }
                },
                pwa: {
                    updateAvailable: 'تحديث متاح',
                    updateMessage: 'يتوفر إصدار جديد من التطبيق. هل ترغب في التحديث الآن؟',
                },
                nav: {
                    sections: {
                        myLearning: 'تعلّمي',
                        content: 'المحتوى',
                        liveAndAttendance: 'البث المباشر والحضور',
                        people: 'الأشخاص',
                        operations: 'العمليات',
                        settings: 'الإعدادات',
                    },
                    dashboard: 'لوحة الإحصائيات',
                    myCourses: 'الدورات',
                    myExams: 'الامتحانات',
                    myLiveRooms: 'غرفي الأونلاين',
                    myProfile: 'ملفي الشخصي',
                    myProfileShort: 'ملفي',
                    subjects: 'المواد',
                    courses: 'الدورات',
                    questionsBanks: 'بنوك الأسئلة',
                    questions: 'الأسئلة',
                    exams: 'الامتحانات',
                    pricingPlans: 'خطط التسعير',
                    licenses: 'التراخيص',
                    enrollmentRequests: 'طلبات التسجيل',
                    profile: 'الملف الشخصي',
                    tenantBranding: 'الهوية البصرية',
                    tenantIntegrations: 'التكاملات',
                    tenantPaymentChannels: 'وسائل الدفع',
                    users: 'الموظفين',
                    students: 'الطلاب',
                    studentResults: 'نتائج الطلاب',
                    liveRooms: 'الغرف الأونلاين',
                    attendance: 'الحضور والغياب',
                    financialReports: 'التقارير المالية',
                    roles: 'الوظائف',
                    more: 'المزيد',
                    rooms: 'الغرف',
                    home: 'الرئيسية',
                    attendanceShort: 'الحضور'
                },
                auth: {
                    login: {
                        welcome: 'مرحبًا بعودتك إلى منصتك',
                        title: 'تسجيل الدخول',
                        email: 'البريد الإلكتروني أو اسم المستخدم',
                        emailPlaceholder: 'اسم المستخدم أو البريد الإلكتروني',
                        password: 'كلمة المرور',
                        loginButton: 'تسجيل الدخول',
                        rememberMe: 'تذكرني',
                        errors: {
                            loginError: 'حدث خطأ أثناء تسجيل الدخول. يرجى المحاولة مرة أخرى',
                            emailRequired: 'البريد الإلكتروني مطلوب',
                        },
                        google: {
                            noAccount: 'لم نجد حسابًا مرتبطًا ببريد جوجل هذا. يرجى التسجيل أولاً لإنشاء حساب.',
                        },
                        selectTenant: {
                            title: 'اختر المنصة',
                            subtitle: 'أنت مسجل في أكثر من منصة. يرجى اختيار واحدة للمتابعة',
                            searchPlaceholder: 'ابحث عن منصة...',
                            noResults: 'لا توجد منصات مطابقة لبحثك',
                            backToLogin: 'العودة لتسجيل الدخول',
                        },
                        noAccountPrompt: 'ليس لديك حساب طالب؟',
                        registerNow: 'سجّل كطالب',
                    },
                    register: {
                        title: 'إنشاء حساب طالب',
                        subtitle: 'سجّل لتبدأ التعلم مع {{name}}',
                        fallbackTenantName: 'منصتك',
                        submitButton: 'تسجيل',
                        successTitle: 'تم التسجيل بنجاح',
                        successMessage: 'تم إنشاء حسابك بنجاح. يمكنك الآن تسجيل الدخول',
                        goToLogin: 'الذهاب لتسجيل الدخول',
                        invalidLinkTitle: 'رابط التسجيل غير صالح',
                        invalidLinkMessage: 'رابط التسجيل هذا مفقود أو غير صالح. يرجى العودة واستخدام رابط التسجيل من صفحة منصتك',
                        backToLogin: 'العودة لتسجيل الدخول',
                        confirmPassword: 'تأكيد كلمة المرور',
                        passwordsDoNotMatch: 'كلمتا المرور غير متطابقتين',
                        whatsAppNumber: 'رقم الواتساب الخاص بك',
                        usernamePlaceholder: 'اسم_المستخدم',
                        gradeSearchPlaceholder: 'ابحث عن الصف...',
                        noGrades: 'لا توجد صفوف مطابقة',
                        google: {
                            completeTitle: 'خطوة أخيرة يا {{name}}',
                            completeSubtitle: 'نحتاج فقط رقم هاتفك والصف الدراسي لإتمام إعداد حسابك',
                            existingAccountTitle: 'أهلاً بعودتك',
                            existingAccountMessage: 'لديك بالفعل حساب في أكثر من مركز — يرجى المتابعة من صفحة تسجيل الدخول.',
                        },
                    },
                    signOut: 'تسجيل الخروج',
                    signIn: 'تسجيل الدخول',
                },
                dashboard: {
                    systemName: 'منصة إتقان',
                    welcome: 'مرحبًا بك في {{name}}',
                    noData: 'لا توجد بيانات متاحة',
                    sections: {
                        contentStatistics: 'إحصائيات المحتوى',
                        userStatistics: 'إحصائيات الموظفين',
                        licenseStatistics: 'إحصائيات التراخيص',
                        examPerformance: 'أداء الامتحانات',
                        revenueStatistics: 'إحصائيات الإيرادات',
                        courseProgress: 'تقدم الدورات',
                        recentActivity: 'النشاط الأخير (آخر 30 يوم)',
                        studentEngagement: 'تفاعل الطلاب',
                        usageLimits: 'الاستخدام والحدود',
                    },
                    usage: {
                        students: 'الطلاب',
                        courses: 'الدورات',
                        exams: 'الامتحانات',
                        storage: 'التخزين',
                        aiTokens: 'الذكاء الاصطناعي (شهريًا)',
                        aiTokensUnit: 'عملة',
                        unlimited: 'غير محدود',
                    },
                    statistics: {
                        subjects: 'المواد',
                        courses: 'الدورات',
                        questionBanks: 'بنوك الأسئلة',
                        questions: 'الأسئلة',
                        exams: 'الامتحانات',
                        chapters: 'الدروس',
                        courseItems: 'عناصر الدورات',
                        totalStudents: 'إجمالي الطلاب',
                        activeStudents: 'الطلاب النشطين',
                        totalUsers: 'إجمالي الموظفين',
                        activeUsers: 'الموظفين النشطين',
                        totalLicenses: 'إجمالي التراخيص',
                        activeLicenses: 'التراخيص النشطة',
                        expiredLicenses: 'التراخيص المنتهية',
                        revokedLicenses: 'التراخيص الملغاة',
                        totalExamAttempts: 'إجمالي محاولات الامتحانات',
                        passedExamAttempts: 'المحاولات الناجحة',
                        failedExamAttempts: 'المحاولات الفاشلة',
                        revenueThisMonth: 'إيرادات هذا الشهر (التراخيص + الغرف الأونلاين المدفوعة)',
                        revenueThisYear: 'إيرادات هذا العام (التراخيص + الغرف الأونلاين المدفوعة)',
                        newLicensesThisMonth: 'تراخيص جديدة هذا الشهر',
                        newLicensesThisYear: 'تراخيص جديدة هذا العام',
                        enrolledCourses: 'الدورات المسجلة',
                        completedItems: 'العناصر المكتملة',
                        totalItems: 'إجمالي العناصر',
                        overallProgress: 'التقدم الإجمالي',
                        itemsCompleted: 'عنصر مكتمل',
                        examAttempts: 'محاولات الامتحانات',
                        passedExams: 'الامتحانات الناجحة',
                        failedExams: 'الامتحانات الفاشلة',
                        averageScore: 'متوسط الدرجات',
                        excellent: 'ممتاز!',
                        topPerformer: 'أداء متميز!',
                        activeCourseLicenses: 'تراخيص الدورات النشطة',
                        activeExamLicenses: 'تراخيص الامتحانات النشطة',
                        recentlyAddedCourses: 'الدورات المضافة حديثاً',
                        recentlyAddedQuestions: 'الأسئلة المضافة حديثاً',
                        recentlyAddedExams: 'الامتحانات المضافة حديثاً',
                        activeStudentsToday: 'الطلاب النشطين اليوم',
                    }
                },
                subject: {
                    subjectsTree: 'شجرة المواد',
                    subjects: 'المواد',
                    subjectsDescription: 'نقطة الانطلاق الأساسية لجميع المواد',
                    addSubjectDescription: 'إنشاء مادة جديدة في المنهج الدراسي',
                    updateSubjectDescription: 'تعديل بيانات المادة',
                    addChapterDescription: 'إضافة درس جديد للمادة',
                    updateChapterDescription: 'تعديل بيانات الدرس',
                    addSubject: 'إضافة مادة',
                    addChapter: 'إضافة درس',
                    updateSubject: 'تعديل المادة',
                    updateChapter: 'تعديل الدرس',
                    deleteSubject: 'حذف المادة',
                    deleteChapter: 'حذف الدرس',
                    title: 'العنوان',
                    description: 'الوصف',
                    grade: 'الصف',
                    deleteSubjectConfirmTitle: 'تأكيد حذف المادة',
                    deleteSubjectConfirmDescription: 'هل أنت متأكد من أنك تريد حذف المادة "{{name}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    deleteChapterConfirmTitle: 'تأكيد حذف الدرس',
                    deleteChapterConfirmDescription: 'هل أنت متأكد من أنك تريد حذف الدرس "{{name}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    gradeLabels: {
                        general: 'عام',
                        primaryFirst: 'الصف الأول الابتدائي',
                        primarySecond: 'الصف الثاني الابتدائي',
                        primaryThird: 'الصف الثالث الابتدائي',
                        primaryFourth: 'الصف الرابع الابتدائي',
                        primaryFifth: 'الصف الخامس الابتدائي',
                        primarySixth: 'الصف السادس الابتدائي',
                        preparatoryFirst: 'الصف الأول الإعدادي',
                        preparatorySecond: 'الصف الثاني الإعدادي',
                        preparatoryThird: 'الصف الثالث الإعدادي',
                        secondaryFirst: 'الصف الأول الثانوي (أو ما يعادله)',
                        secondarySecond: 'الصف الثاني الثانوي (أو ما يعادله)',
                        secondaryThird: 'الصف الثالث الثانوي (أو ما يعادله)',
                        universityFirst: 'الفرقة الأولى الجامعية',
                        universitySecond: 'الفرقة الثانية الجامعية',
                        universityThird: 'الفرقة الثالثة الجامعية',
                        universityFourth: 'الفرقة الرابعة الجامعية',
                        universityFifth: 'الفرقة الخامسة الجامعية',
                    },
                    errors: {
                        titleRequired: 'العنوان مطلوب',
                        titleMaxLength: 'العنوان يجب أن يحتوي على الأكثر {{max}} حرفًا',
                        descriptionRequired: 'الوصف مطلوب',
                        descriptionMaxLength: 'الوصف يجب أن يحتوي على الأكثر {{max}} حرفًا',
                        gradeAllowedValues: 'الصف يجب أن يكون من القيم المسموح بها',
                    }
                },
                pricingPlan: {
                    gridTitle: 'خطط التسعير',
                    addPricingPlan: 'إضافة خطة تسعير',
                    title: 'العنوان',
                    description: 'الوصف',
                    price: 'السعر',
                    timeUnit: 'الوحدة الزمنية',
                    currency: 'العملة',
                    grade: 'الصف',
                    isActive: 'نشطة حاليًا؟',
                    isPopular: 'تحديد كالأكثر شيوعًا',
                    editPricingPlan: 'تحديث خطة تسعير',
                    deletePricingPlan: 'حذف خطة تسعير',
                    deletePricingPlanConfirmTitle: 'تأكيد حذف خطة تسعير',
                    deletePricingPlanConfirmDescription: 'هل أنت متأكد من أنك تريد حذف خطة التسعير "{{title}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    active: 'نشطة',
                    inactive: 'غير نشطة',
                    timeUnits: {
                        month: 'شهر',
                        term: 'فصل دراسي',
                        fullAcademicYear: 'عام دراسي كامل',
                    },
                    gridDescription: 'إدارة وتنظيم خطط الاشتراك والتسعير',
                    popular: 'الأكثر شيوعًا',
                    levels: {
                        general: 'عام',
                        primary: 'ابتدائي',
                        preparatory: 'إعدادي',
                        secondary: 'ثانوي',
                        university: 'جامعي',
                    },
                    errors: {
                        titleRequired: 'العنوان مطلوب',
                        titleMaxLength: 'يجب ألا يتجاوز العنوان {{max}} حرفًا',
                        descriptionRequired: 'الوصف مطلوب',
                        descriptionMaxLength: 'يجب ألا يتجاوز الوصف {{max}} حرفًا',
                        priceMinValue: 'السعر يجب أن يكون على الأقل {{min}}',
                        priceMaxValue: 'السعر يجب أن يكون على الأكثر {{max}}',
                        currencyAllowedValues: 'العملة يجب أن تكون من القيم المسموح بها',
                        gradeAllowedValues: 'الصف يجب أن يكون من القيم المسموح بها',
                        timeUnitAllowedValues: 'الوحدة الزمنية يجب أن تكون من القيم المسموح بها',
                        noPlansAdded: 'لا توجد خطط تسعير بعد',
                        noPlansAddedSubtitle: 'أنشئ أول خطة تسعير للبدء',
                    }
                },
                enrollment: {
                    coursesCatalogTitle: 'الدورات',
                    coursesCatalogSubtitle: 'تصفح الدورات المتاحة لصفك الدراسي وسجّل أو أرسل طلب تسجيل',
                    examsCatalogTitle: 'الامتحانات',
                    examsCatalogSubtitle: 'تصفح الامتحانات المتاحة لصفك الدراسي وسجّل أو أرسل طلب تسجيل',
                    states: {
                        notEnrolled: 'غير مسجل',
                        pending: 'قيد الانتظار',
                        enrolled: 'تم التسجيل',
                    },
                    free: 'مجاني',
                    priceFormat: '{{price}} {{currency}}',
                    enrollNow: 'سجّل الآن',
                    requestEnrollment: 'طلب تسجيل',
                    cancelRequest: 'إلغاء الطلب',
                    goToMyCourses: 'الذهاب إلى دوراتي',
                    goToMyExams: 'الذهاب إلى امتحاناتي',
                    cancelRequestConfirmTitle: 'إلغاء طلب التسجيل',
                    cancelRequestConfirmDescription: 'هل أنت متأكد من رغبتك في إلغاء طلب التسجيل الخاص بـ "{{title}}"؟',
                    noCoursesAvailable: 'لا توجد دورات متاحة',
                    noCoursesAvailableSubtitle: 'لا توجد دورات متاحة لصفك الدراسي حتى الآن',
                    noExamsAvailable: 'لا توجد امتحانات متاحة',
                    noExamsAvailableSubtitle: 'لا توجد امتحانات متاحة لصفك الدراسي حتى الآن',
                    requestDialog: {
                        title: 'طلب تسجيل',
                        paymentProofLabel: 'إثبات الدفع (اختياري)',
                        uploadPaymentProof: 'رفع إثبات الدفع',
                        confirm: 'إرسال الطلب',
                        totalFormula: '{{count}} × {{unitPrice}} {{currency}} =',
                        paymentChannelsLabel: 'وسيلة الدفع المستخدمة',
                        noPaymentChannels: 'لم يقم المدرّس بإعداد أي وسيلة دفع بعد — تواصل معه مباشرة لترتيب الدفع.',
                    },
                },
                enrollmentRequest: {
                    pageTitle: 'طلبات التسجيل',
                    pageDescription: 'مراجعة طلبات تسجيل الطلاب والرد عليها',
                    tableTitle: 'الطلبات',
                    status: 'الحالة',
                    student: 'الطالب',
                    grade: 'الصف',
                    requestedAt: 'تاريخ الطلب',
                    requested: 'المطلوب',
                    monthsCount: '{{count}} شهر',
                    attemptsCountShort: '{{count}} محاولة',
                    paymentProof: 'إثبات الدفع',
                    viewPaymentProof: 'عرض إثبات الدفع',
                    noRequestsFound: 'لا توجد طلبات تسجيل',
                    approve: 'موافقة',
                    reject: 'رفض',
                    forStudent: 'طلب من {{name}}',
                    paidVia: 'تم الدفع عبر {{channel}}',
                    statusLabels: {
                        pending: 'قيد الانتظار',
                        approved: 'تمت الموافقة',
                        rejected: 'مرفوض',
                        cancelledByStudent: 'ألغاه الطالب',
                    },
                    aiVerdictLabel: 'فحص الذكاء الاصطناعي',
                    aiAutoApproved: 'تمت الموافقة تلقائيًا بواسطة الذكاء الاصطناعي',
                    aiVerdict: {
                        match: 'مطابقة',
                        amountMismatch: 'عدم تطابق المبلغ',
                        channelMismatch: 'عدم تطابق وسيلة الدفع',
                        unreadable: 'صورة غير واضحة',
                        uncertain: 'غير مؤكد',
                        duplicateProof: 'صورة مكررة',
                        rateLimited: 'تم تجاوز الحد المسموح',
                    },
                    approveDialog: {
                        title: 'الموافقة على طلب التسجيل',
                        accessMonthsCount: 'مدة الوصول (بالأشهر)',
                        attemptsCount: 'عدد المحاولات',
                    },
                    rejectDialog: {
                        title: 'رفض طلب التسجيل',
                        noteLabel: 'السبب (اختياري)',
                    },
                },
                studentExam: {
                    pageTitle: 'الامتحانات',
                    pageDescription: 'امتحاناتك — المسجل بها والكتالوج الكامل للتصفح',
                    enterExam: 'ادخل الامتحان',
                    enrolledTab: 'المسجل بها',
                    browseTab: 'تصفح الكل',
                    noExamsAvailable: 'لا توجد امتحانات بعد',
                    noExamsAvailableSubtitle: 'لم تسجّل في أي امتحان بعد — تصفح الامتحانات المتاحة أدناه.',
                    browseExamsButton: 'تصفح الامتحانات',
                    questionsCount: '{{count}} أسئلة',
                    timeLimit: '{{minutes}} دقيقة',
                    timeLimitUnlimited: 'مفتوح',
                    totalScore: 'المجموع: {{score}} نقطة',
                    passingScore: 'النجاح: {{score}} نقطة',
                    previousAttempt: 'الدرجة السابقة: {{score}}/{{total}}',
                    submitExam: 'تسليم الامتحان',
                    passed: 'ناجح',
                    failed: 'راسب',
                    correctAnswersCount: '{{correct}} من {{total}} صحيحة',
                    question: 'سؤال',
                    points: 'نقاط',
                    hint: 'تلميح',
                    correct: 'صحيح',
                    incorrect: 'خطأ',
                    pointsEarned: '{{earned}}/{{max}} نقطة',
                    progress: '{{answered}} من {{total}} أسئلة تمت الإجابة عليها',
                    previous: 'السابق',
                    next: 'التالي',
                    noQuestions: 'هذا الامتحان لا يحتوي على أسئلة',
                    backToExams: 'العودة للامتحانات',
                    questionNavigation: 'تنقل الأسئلة',
                    legendAnswered: 'تمت الإجابة',
                    legendUnanswered: 'لم تتم الإجابة',
                    legendFlagged: 'مُعلّم للمراجعة',
                    flagForReview: 'علّم للمراجعة',
                    confirmSubmitTitle: 'تأكيد التسليم',
                    confirmSubmitMessage: 'هل أنت متأكد من تسليم الامتحان؟ لا يمكنك تغيير إجاباتك بعد التسليم',
                    unansweredWarning: 'لديك {{count}} سؤال/أسئلة لم تتم الإجابة عليها',
                    timeUpAutoSubmit: 'انتهى الوقت! تم تسليم امتحانك تلقائياً',
                    fullscreen: 'وضع ملء الشاشة',
                    exitFullscreen: 'الخروج من ملء الشاشة',
                    attemptsInfo: '{{used}} / {{max}} محاولات مستخدمة',
                    noAttemptsLeft: 'لا توجد محاولات متبقية',
                    viewPastResults: 'عرض النتائج',
                    pastResultsTitle: 'المحاولات السابقة — {{examTitle}}',
                    attemptNumber: 'المحاولة رقم {{number}}',
                    questionOf: 'سؤال {{current}} من {{total}}',
                    answeredCount: '{{count}} تمت الإجابة',
                    unansweredCount: '{{count}} لم تتم الإجابة',
                    flaggedCount: '{{count}} مُعلّم',
                    reviewAnswers: 'مراجعة الإجابات',
                    yourScore: 'درجتك',
                    congratulations: 'تهانينا! لقد نجحت!',
                    betterLuckNextTime: 'لم تجتز هذه المرة',
                    correctCount: '{{correct}}/{{total}} صحيحة',
                    passingScoreRequired: 'النجاح: {{score}} نقطة',
                    summary: 'ملخص',
                    examInfo: 'معلومات الامتحان',
                    examinee: 'الممتحن',
                    totalPointsLabel: '{{points}} نقطة إجمالي',
                    timeLimitLabel: '{{minutes}} دقيقة',
                    noTimeLimitLabel: 'بدون وقت محدد',
                    filterAll: 'الكل',
                    filterUnanswered: 'لم تُجب',
                    filterFlagged: 'مُعلّم',
                    increaseFontSize: 'تكبير الخط',
                    decreaseFontSize: 'تصغير الخط',
                    eliminateOption: 'انقر يميناً للاستبعاد',
                    eliminateOptionMobile: 'اضغط مطولاً للاستبعاد',
                    optionEliminated: 'مُستبعد',
                    shortcuts: 'اختصارات',
                    shortcutNavigate: 'تنقل',
                    shortcutFlag: 'تعليم السؤال',
                    shortcutJump: 'انتقل للسؤال #',
                    timeSpent: 'الوقت: {{time}}',
                    timeSpentSeconds: '{{seconds}}ث',
                    timeSpentMinutes: '{{minutes}}د {{seconds}}ث',
                    typeMCSA: 'إجابة واحدة',
                    typeMCMA: 'إجابات متعددة',
                    typeTOrF: 'صح أو خطأ',
                    typeEssay: 'مقالي',
                    typeMentalMathSheet: 'ورقة الحساب الذهني',
                    essayAnswerPlaceholder: 'اكتب إجابتك هنا...',
                    mentalMathAnswerPlaceholder: 'الإجابة',
                    mentalMathCorrectAnswer: 'الإجابة الصحيحة:',
                    aiGradingUnavailable: 'تعذر التصحيح التلقائي — تم تسجيل درجة 0',
                    aiGradedFeedback: 'الملاحظات',
                    examGateRulesTitle: 'قواعد الامتحان',
                    examGateRule1: 'يعمل الامتحان في وضع ملء الشاشة — لا تخرج منه حتى تسليم الامتحان',
                    examGateRule2: 'أي محاولة لمغادرة الامتحان أو تعطيله تُرصد وتُسجَّل',
                    examGateStart: 'ابدأ الامتحان (ملء الشاشة)',
                    examGateContinue: 'تابع الامتحان (ملء الشاشة)',
                    progressRestored: 'تم استعادة تقدمك السابق',
                    fullscreenLostTitle: 'غادرت شاشة الامتحان',
                    fullscreenLostMessage: 'تم الخروج من وضع ملء الشاشة، انقر على الزر أدناه للعودة إلى الامتحان',
                    fullscreenLostReturn: 'العودة إلى الامتحان',
                    violationsCount: '{{count}} مخالفة',
                    tabSwitchWarning: 'تحذير: تم رصد تبديل التبويب وتسجيله كمخالفة',
                },
                studentCourse: {
                    pageTitle: 'الدورات',
                    pageDescription: 'دوراتك — المسجل بها والكتالوج الكامل للتصفح',
                    startCourse: 'ابدأ الدورة',
                    continueCourse: 'أكمل الدورة',
                    enrolledTab: 'المسجل بها',
                    browseTab: 'تصفح الكل',
                    noCoursesEnrolled: 'لا توجد دورات بعد',
                    noCoursesEnrolledSubtitle: 'لم تسجّل في أي دورة بعد — تصفح الدورات المتاحة أدناه.',
                    browseCoursesButton: 'تصفح الدورات',
                    accessPeriod: 'متاح من {{from}} إلى {{to}}',
                    courseProgress: 'تقدم الدورة',
                    itemsCompleted: '{{completed}} من {{total}} عناصر مكتملة',
                    backToMyCourses: 'العودة إلى دوراتي',
                    watchVideo: 'مشاهدة الفيديو',
                    videoComplete: 'اكتمل الفيديو',
                    replayVideo: 'إعادة التشغيل',
                    videoQuality: 'الجودة',
                    videoQualityAuto: 'تلقائي',
                    viewMaterial: 'عرض الملف',
                    startQuiz: 'بدء الامتحان',
                    questions: 'أسئلة',
                    score: 'الدرجة',
                    section: 'القسم',
                    courseOverview: 'نظرة عامة على الدورة',
                    selectItem: 'اختر عنصراً من القائمة الجانبية للبدء',
                    totalItems: 'إجمالي العناصر',
                    completedItems: 'مكتمل',
                    openMaterial: 'فتح الملف',
                    materialDescription: 'تحميل أو عرض هذا المحتوى التعليمي',
                    quizInfo: 'امتحان - {{count}} أسئلة',
                    previous: 'السابق',
                    next: 'التالي',
                    showSidebar: 'عناصر الدورة',
                    quiz: {
                        question: 'سؤال',
                        points: 'نقاط',
                        hint: 'تلميح',
                        correct: 'صحيح',
                        incorrect: 'خطأ',
                        pointsEarned: '{{earned}}/{{max}} نقطة',
                        mentalMathAnswerPlaceholder: 'الإجابة',
                        mentalMathCorrectAnswer: 'الإجابة الصحيحة:',
                        progress: '{{answered}} من {{total}} أسئلة تمت الإجابة عليها',
                        resultsTitle: 'نتائج الامتحان',
                        scoreResult: 'درجتك: {{earned}}/{{total}} نقطة',
                        correctAnswers: 'الإجابات الصحيحة: {{correct}}/{{total}}',
                        previous: 'السابق',
                        next: 'التالي',
                        submitQuiz: 'تسليم الامتحان',
                        confirmSubmitTitle: 'تسليم الامتحان',
                        confirmSubmitDescription: 'هل أنت متأكد من تسليم الامتحان؟ لن تتمكن من تغيير إجاباتك',
                        noQuestions: 'هذا الامتحان لا يحتوي على أسئلة',
                        quizCompleted: 'تم إكمال الامتحان!',
                        quizTimeLimitDisplay: '{{minutes}} دقيقة',
                        quizTimeUpAutoSubmit: 'انتهى الوقت! تم تسليم الامتحان تلقائياً',
                    },
                },
                course: {
                    gridTitle: 'الدورات',
                    gridDescription: 'إدارة وتنظيم الدورات التعليمية',
                    addCourseDescription: 'إنشاء دورة تعليمي جديد',
                    editCourseDescription: 'تحديث بيانات الدورة',
                    active: 'نشطة',
                    inactive: 'غير نشطة',
                    month: 'شهر',
                    free: 'مجاني',
                    coursesCount: 'دورة',
                    videosShort: 'فيديو',
                    quizzesShort: 'امتحانات',
                    materialsShort: 'ملفات',
                    noResources: 'لا توجد موارد',
                    videos: '{{count}} فيديو',
                    quizzes: '{{count}} امتحان قصير',
                    materials: '{{count}} ملف',
                    academicYear: 'العام الدراسي',
                    difficultyLevel: 'مستوى الصعوبة',
                    accessType: 'نوع الوصول',
                    durationFormat: 'ساعة',
                    editCourse: 'تعديل الدورة',
                    deleteCourse: 'حذف الدورة',
                    deleteCourseConfirmTitle: 'تأكيد حذف الدورة',
                    deleteCourseConfirmDescription: 'هل أنت متأكد من أنك تريد حذف الدورة "{{title}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    addCourse: 'إضافة دورة',
                    objective: 'الهدف',
                    title: 'العنوان',
                    subject: 'المادة',
                    durationInHours: 'مدة الدورة بالساعة',
                    subscriptionPerMonth: 'الاشتراك / الشهر',
                    currency: 'العملة',
                    isActive: 'نشطة حاليًا؟',
                    thumbnail: 'صورة الغلاف',
                    language: 'لغة الدورة',
                    languageArabic: 'عربي',
                    languageEnglish: 'إنجليزي',
                    difficultyLevelLabels: {
                        everyone: 'الجميع',
                        beginner: 'مبتدئ',
                        intermediate: 'متوسط',
                        advanced: 'متقدم',
                    },
                    itemTypeLabels: {
                        video: 'فيديو',
                        quiz: 'امتحان قصير',
                        material: 'ملف',
                    },
                    resourceAccessTypeLabels: {
                        open: 'مفتوح',
                        requiresLicense: 'يتطلب رخصة',
                    },
                    courseItem: {
                        courseItems: 'عناصر الدورة',
                        courseItemsDescription: 'إدارة وتنظيم محتوى الدورة',
                        addCourseItems: 'إضافة عناصر للدورة',
                        addCourseItem: 'إضافة عنصر للدورة',
                        updateCourseItem: 'تعديل عنصر الدورة',
                        title: "العنوان",
                        objective: "الهدف",
                        itemType: "نوع العنصر",
                        pointsPerQuestion: "درجة كل سؤال",
                        quizTimeLimit: "الحد الزمني للامتحان (بالدقائق)",
                        quizTimeLimitHelper: "اتركها فارغة في حالة عدم وجود حد زمني",
                        lockedForEditing: 'يحتوي هذا الامتحان على إجابات سابقة من الطلاب، ولا يمكن تعديل إلا العنوان والهدف والفاصل',
                        question: "سؤال",
                        questions: "الأسئلة",
                        video: "فيديو",
                        material: "ملف",
                        youtubeSource: "يوتيوب",
                        uploadSource: "رفع فيديو",
                        hasDivider: "يحتوي على خط فاصل (كنهاية قسم)",
                        endOfSection: "نهاية القسم",
                        editCourseItem: "تعديل عنصر الدورة",
                        preview: {
                            previewCourseItem: "معاينة عنصر الدورة",
                            previewCourseItemDescription: "معاينة كيفية ظهور هذا العنصر للطلاب",
                            openMaterial: "فتح الملف",
                        },
                        deleteCourseItem: "حذف عنصر الدورة",
                        deleteCourseItemConfirmTitle: "تأكيد حذف عنصر الدورة",
                        deleteCourseItemConfirmDescription: 'هل أنت متأكد من حذف عنصر الدورة "{{title}}"؟ هذه الخطوة لا يمكن التراجع عنها',
                        addCourseItemDescription: 'إضافة عنصر جديد للدورة',
                        updateCourseItemDescription: 'تحديث بيانات عنصر الدورة',
                        errors: {
                            titleRequired: 'العنوان مطلوب',
                            titleMaxLength: 'يجب ألا يتجاوز العنوان {{max}} حرفًا',
                            objectiveRequired: 'الهدف مطلوب',
                            objectiveMaxLength: 'يجب ألا يتجاوز الهدف {{max}} حرفًا',
                            itemTypeAllowedValues: 'نوع المورد يجب أن يكون من القيم المسموح بها',
                            resourceTypeMismatch: 'نوع مورد عنصر الدورة يجب أن يكون مطابقاً مع نوع المورد المختار بالفعل',
                            videoResourceUriRequired: 'يجب اختيار فيديو يوتيوب',
                            videoUploadRequired: 'يجب رفع فيديو',
                            videoUploadInProgress: 'يرجى الانتظار حتى يكتمل رفع الفيديو',
                            pointsPerQuestionPositive: 'درجة كل سؤال يجب أن تكون رقمًا أكبر من الصفر',
                            questionsIdsRequired: 'يجب اختيار على الأقل سؤال وحيد',
                            timeLimitNonnegative: 'الحد الزمني يجب أن يكون صفرًا أو رقمًا موجبًا',
                            materialResourceRelativeUriRequired: 'يجب رفع ملف',
                            noCourseItems: 'لا توجد عناصر لهذا الدورة بعد',
                            noCourseItemsSubtitle: 'ابدأ بإضافة أول عنصر للدورة',
                        }
                    },
                    errors: {
                        titleRequired: 'العنوان مطلوب',
                        titleMaxLength: 'يجب ألا يتجاوز العنوان {{max}} حرفًا',
                        objectiveRequired: 'الهدف مطلوب',
                        objectiveMaxLength: 'يجب ألا يتجاوز الهدف {{max}} حرفًا',
                        subjectIdValue: 'المادة مطلوبة',
                        durationValue: 'المدة يجب أن تكون عددًا أكبر من الصفر',
                        subscriptionValue: 'الاشتراك يجب أن يكون أكبر من الصفر عندما يكون نوع الوصول "يتطلب رخصة"',
                        academicYearRequired: 'السنة الدراسية مطلوبة',
                        academicYearMaxLength: 'يجب ألا تتجاوز السنة الدراسية {{max}} حرفًا',
                        difficultyLevelAllowedValues: 'مستوى الصعوبة يجب أن يكون من القيم المسموح بها',
                        accessTypeAllowedValues: 'نوع الوصول يجب أن يكون من القيم المسموح بها',
                        currencyAllowedValues: 'العملة يجب أن يكون من القيم المسموح بها',
                        noCoursesAdded: 'لا توجد دورات',
                        noCoursesAddedSubtitle: 'أضف دورات جديدة لكي تظهر هنا',
                        courseNotLoaded: 'لم يتم تحميل الدورة',
                    }
                },
                question: {
                    tableTitle: 'الأسئلة',
                    questionsDescription: 'إنشاء وإدارة أسئلة التقييم',
                    addQuestionDescription: 'إنشاء سؤال تقييم جديد',
                    editQuestionDescription: 'تحديث بيانات سؤال التقييم',
                    code: 'الكود',
                    text: 'النص',
                    type: 'النوع',
                    difficultyLevel: 'مستوى الصعوبة',
                    hint: 'الإرشاد',
                    modelAnswer: 'الإجابة النموذجية',
                    modelAnswerHelp: 'الإجابة المرجعية/الصحيحة. يقارن الذكاء الاصطناعي معنى إجابة الطالب بها عند التصحيح التلقائي (اختياري)',
                    essayNoOptionsNote: 'يجيب الطلاب بنص حر. يتم تصحيح هذا السؤال تلقائيًا بواسطة الذكاء الاصطناعي.',
                    mentalMathSheetNote: 'أنشئ ورقة من الأعمدة — كل عمود سلسلة من الأرقام مرتبطة بعامل تشغيل واحد. الإجابة الصحيحة لكل عمود تُحسب دائمًا بواسطة النظام، ولا تُدخل يدويًا.',
                    mentalMathColumns: 'الأعمدة',
                    mentalMathColumn: 'العمود {{number}}',
                    operator: 'عامل التشغيل',
                    operand: 'الرقم {{number}}',
                    addOperand: 'إضافة رقم',
                    removeOperand: 'إزالة رقم',
                    addColumn: 'إضافة عمود',
                    removeColumn: 'إزالة عمود',
                    correctAnswerPreview: 'الإجابة:',
                    invalidColumnPreview: 'الإجابة: —',
                    arithmeticOperatorLabels: {
                        add: 'جمع (+)',
                        subtract: 'طرح (−)',
                        multiply: 'ضرب (×)',
                        divide: 'قسمة (÷)',
                    },
                    optionsRandomized: 'ظهور الخيارات في ترتيب عشوائي في الامتحانات',
                    options: 'الخيارات',
                    option: 'الخيار',
                    isCorrect: 'الإجابة الصحيحة',
                    removeOption: 'إزالة الخيار',
                    addOption: 'إضافة خيار',
                    addQuestion: 'إضافة سؤال',
                    questionsBank: 'بنك الأسئلة',
                    viewQuestion: 'عرض السؤال',
                    viewQuestionDescription: 'عرض تفاصيل السؤال',
                    editQuestion: 'تعديل سؤال',
                    deleteQuestion: 'حذف سؤال',
                    aiGenerated: 'مُولَّد بالذكاء الاصطناعي',
                    manualSource: 'مُدخَل يدويًا',
                    source: 'المصدر',
                    deleteQuestionConfirmTitle: 'تأكيد حذف سؤال',
                    deleteQuestionConfirmDescription: 'هل أنت متأكد من أنك تريد حذف السؤال "{{text}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    difficultyLevelLabels: {
                        easy: 'سهل',
                        medium: 'متوسط',
                        hard: 'صعب',
                        veryHard: 'صعب جدًا',
                    },
                    typeLabels: {
                        mcsa: 'اختيار من متعدد - إجابة واحدة',
                        mcma: 'اختيار من متعدد - إجابات متعددة',
                        tOrF: 'صح أو خطأ',
                        essay: 'مقالي (تصحيح بالذكاء الاصطناعي)',
                        mentalMathSheet: 'ورقة الحساب الذهني',
                    },
                    trueFalseOptions: {
                        true: 'صح',
                        false: 'خطأ',
                    },
                    errors: {
                        textRequired: 'النص مطلوب',
                        typeAllowedValues: 'النوع يجب أن يكون من القيم المسموح بها',
                        difficultyLevelAllowedValues: 'مستوى الصعوبة يجب أن يكون من القيم المسموح بها',
                        questionsBankRequired: 'بنك الأسئلة مطلوب',
                        hintMaxLength: 'الإرشاد يجب أن يحتوي على الأكثر {{max}} حرفًا',
                        modelAnswerMaxLength: 'الإجابة النموذجية يجب أن تحتوي على الأكثر {{max}} حرفًا',
                        noQuestionsFound: 'لا توجد أسئلة',
                        minimumTwoOptions: 'يجب أن يكون هناك على الأقل خياران',
                        trueFalseOptionsInvalid: 'يجب اختيار إجابة واحدة من بين صح أو خطأ',
                        exactlyOneCorrectOption: 'يجب اختيار إجابة واحدة صحيحة فقط',
                        atLeastOneCorrectOption: 'يجب اختيار إجابة واحدة صحيحة على الأقل',
                        essayHasNoOptions: 'أسئلة المقال لا يمكن أن تحتوي على خيارات',
                        mentalMathColumnsRequired: 'يتطلب سؤال ورقة الحساب الذهني عمودًا واحدًا على الأقل',
                        minimumTwoOperands: 'يتطلب العمود رقمين على الأقل',
                        operatorsCountMismatch: 'عوامل التشغيل في العمود لا تتطابق مع أرقامه',
                        singleOperatorOnly: 'يجب أن يستخدم العمود عامل تشغيل واحدًا فقط',
                        mentalMathDivideByZero: 'لا يمكن أن يحتوي العمود على قسمة على صفر',
                    }
                },
                exam: {
                    tableTitle: 'الامتحانات',
                    examsDescription: 'إنشاء وإدارة ومراقبة الامتحانات',
                    addExamDescription: 'إنشاء امتحان جديد',
                    editExamDescription: 'تحديث بيانات الامتحان',
                    lockedForEditing: 'يحتوي هذا الامتحان على محاولات سابقة من الطلاب، ولا يمكن تعديل إلا العنوان والوصف وحالة التفعيل ولغة الامتحان والحد الأقصى للمحاولات (وصول مجاني)',
                    title: 'العنوان',
                    description: 'الوصف',
                    subjectTitle: 'المادة',
                    chapterTitle: 'الدرس',
                    questionsCount: 'عدد الأسئلة',
                    questionsCountHelper: 'يُحسب تلقائيًا من الأسئلة المحددة',
                    timeLimit: 'الوقت المحدد (بالدقيقة)',
                    totalScore: 'إجمالي الدرجات',
                    subscriptionPerAttempt: 'الاشتراك للمحاولة',
                    currency: 'العملة',
                    accessType: 'نوع الوصول',
                    maxFreeAttempts: 'الحد الأقصى للمحاولات (وصول مجاني)',
                    maxFreeAttemptsHelper: 'اتركه فارغًا للسماح بمحاولات غير محدودة لكل طالب',
                    grade: 'الصف',
                    createdAt: 'تاريخ الإنشاء',
                    isActive: 'نشط حاليًا؟',
                    thumbnail: 'صورة الغلاف',
                    addExam: 'إضافة امتحان',
                    subject: 'المادة',
                    chapter: 'الدرس',
                    editExam: 'تعديل امتحان',
                    viewExam: 'عرض الامتحان',
                    viewExamDescription: 'معاينة أسئلة الامتحان وترتيبها وإجاباتها',
                    deleteExam: 'حذف امتحان',
                    deleteExamConfirmTitle: 'تأكيد حذف امتحان',
                    deleteExamConfirmDescription: 'هل أنت متأكد من أنك تريد حذف الامتحان "{{title}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    formTimeLimit: 'الوقت المحدد بالدقائق (0 يعني وقت مفتوح)',
                    passingScore: 'درجة النجاح',
                    randomizeQuestions: 'إظهار الأسئلة في ترتيب عشوائي',
                    language: 'لغة الامتحان',
                    languageArabic: 'عربي',
                    languageEnglish: 'إنجليزي',
                    selectQuestions: 'اختر الأسئلة',
                    selectedQuestions: 'الأسئلة المختارة',
                    questionScore: 'درجة السؤال',
                    questionNumber: 'سؤال رقم',
                    dragToReorder: 'اسحب لإعادة الترتيب',
                    fetchedQuestions: 'الأسئلة المنتقاة',
                    filtrationPanel: 'لوحة التصفية',
                    timeLimitUnlimited: 'مفتوح',
                    copyExam: 'نسخ الامتحان',
                    copyExamConfirmTitle: 'تأكيد نسخ امتحان',
                    copyExamConfirmDescription: 'هل أنت متأكد من أنك تريد نسخ الامتحان "{{title}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    exportResults: 'تصدير النتائج',
                    exportResultsDialog: {
                        title: 'تصدير النتائج — {{title}}',
                        description: 'تنزيل ملف CSV لجميع المحاولات على هذا الامتحان، بحيث يمثل كل صف محاولة واحدة',
                        academicYear: 'العام الدراسي',
                        allAcademicYears: 'كل الأعوام الدراسية',
                        exportButton: 'تصدير',
                        exporting: 'جاري التصدير...',
                        exportError: 'فشل تصدير النتائج. يرجى المحاولة مرة أخرى',
                    },
                    randomizeQuestionsTableHeader: 'ترتيب عشوائي للأسئلة',
                    questionSelectionMethodLabels: {
                        manual: 'يدوي',
                        automatic: 'تلقائي',
                        upload: 'من ملف امتحان',
                    },
                    autoGenerate: {
                        title: 'توليد الأسئلة تلقائيًا',
                        questionsCount: 'عدد الأسئلة',
                        generateButton: 'توليد',
                        requirementsHint: 'يجب اختيار بنك أسئلة واحد على الأقل، ونوع سؤال واحد على الأقل، ومستوى صعوبة واحد على الأقل لتوليد الأسئلة',
                        successMessage: 'تم توليد {{count}} سؤال بنجاح',
                        partialResultsWarning: 'تم العثور على {{generated}} فقط من أصل {{requested}} سؤال مطلوب مطابق لمعاييرك',
                    },
                    upload: {
                        chooseFile: 'اختر ملف الامتحان',
                        fileHelp: 'المقبول: صور (JPG/PNG/WEBP)، حتى 15 ميجابايت.',
                        notes: 'ملاحظات للذكاء الاصطناعي (اختياري)',
                        notesHelp: 'أي سياق إضافي، مثل "تجاهل السؤال 5، فهو غير مكتمل"',
                        extract: 'استخراج الأسئلة',
                        extracting: 'جارٍ الاستخراج…',
                        reviewHeading: 'تم العثور على {{count}} سؤال — راجع وعدّل واختر ما تريد تضمينه',
                        selectedCount: 'تم اختيار {{count}}',
                        errors: {
                            extractionFailed: 'فشل استخراج الأسئلة من الملف المرفوع',
                        },
                    },
                    errors: {
                        noExamsFound: 'لا توجد امتحانات',
                        titleRequired: 'العنوان مطلوب',
                        titleMaxLength: 'العنوان يجب أن يحتوي على الأكثر {{max}} حرفًا',
                        descriptionRequired: 'الوصف مطلوب',
                        descriptionMaxLength: 'الوصف يجب أن يحتوي على الأكثر {{max}} حرفًا',
                        questionsCountMin: 'عدد الأسئلة يجب أن يكون على الأقل 1',
                        timeLimitMin: 'الوقت المحدد يجب أن يكون على الأقل 0',
                        totalScoreMin: 'إجمالي الدرجات يجب أن يكون على الأقل 1',
                        passingScoreMin: 'درجة النجاح يجب أن تكون على الأقل 1',
                        subscriptionPerAttemptMin: 'الاشتراك للمحاولة يجب أن يكون على الأقل 0',
                        currencyAllowedValues: 'العملة يجب أن تكون واحدة من القيم المسموح بها',
                        accessTypeAllowedValues: 'نوع الوصول يجب أن يكون من القيم المسموح بها',
                        maxFreeAttemptsMin: 'الحد الأقصى للمحاولات يجب أن يكون 1 على الأقل',
                        subjectRequired: 'المادة مطلوبة',
                        passingScoreExceedsTotalScore: 'درجة النجاح يجب أن تكون أقل من أو تساوي إجمالي الدرجات',
                        noQuestionsSelected: 'لا توجد أسئلة مختارة',
                        questionsRequired: 'الأسئلة مطلوبة',
                        selectedQuestionsCountNotMatch: 'عدد الأسئلة المختارة يجب أن يساوي عدد الأسئلة المحددة',
                        questionScoreMin: 'درجة السؤال يجب أن تكون على الأقل 1',
                        questionScoreNotMatch: 'إجمالي درجات الأسئلة يجب أن يساوي إجمالي الدرجات المحددة',
                        subscriptionPerAttemptValueInCaseRequiresLicense: 'الاشتراك للمحاولة يجب أن يكون أكبر من 0 في حالة أن نوع الوصول "يتطلب رخصة"',
                        scoreMin: 'الدرجة يجب أن تكون على الأقل 1',
                        difficultyLevelAllowedValues: 'مستوى الصعوبة يجب أن يكون من القيم المسموح بها',
                        questionTypeAllowedValues: 'نوع السؤال يجب أن يكون من القيم المسموح بها',
                        fileHashRequired: 'بصمة الملف مطلوبة',
                        fileNameRequired: 'اسم الملف مطلوب',
                    }
                },
                sunEditor: {
                    insertNewEquation: 'إدراج معادلة جديدة',
                    cancelInsertion: 'إلغاء الإدراج',
                    cancelUpdate: 'إلغاء التحديث',
                    uploadImage: 'صورة',
                    uploadVideo: 'فيديو',
                    uploadAudio: 'صوت',
                    uploading: 'جاري الرفع...',
                    uploadingPercent: 'جاري الرفع... {{percent}}%',
                    equationsEditor: {
                        placeholder: '\\text{أدخل معادلة}',
                        insertEquation: 'إدراج المعادلة',
                        updateEquation: 'تحديث المعادلة',
                        cardTitle: 'محرر المعادلات',
                        previewHeadline: 'معاينة المعادلة',
                        renderEnglish: 'عرض بالإنجليزية',
                        renderArabic: 'عرض بالعربية',
                    },
                    errors: {
                        failedToCaptureScreenshot: 'خطأ في التقاط صورة المعادلة: {{error}}',
                        uploadFailed: 'فشل رفع الملف',
                        fileTooLarge: 'حجم الملف كبير جدًا. الحد الأقصى المسموح به هو {{maxMb}} ميجابايت',
                    }
                },
                fileUploader: {
                    fileUploader: 'رفع ملف',
                    dropOrClick: 'انقر أو اسحب ملفًا هنا للرفع',
                    chooseFile: 'اختر ملف',
                    selectedFile: 'الملف المختار',
                    uploadFile: 'رفع ملف',
                    uploading: 'رفع...',
                    uploadingPercent: 'جاري الرفع... {{percent}}%',
                    viewFile: 'عرض الملف المرفوع',
                    errors: {
                        noFileSelected: 'لا يوجد ملف محدد',
                    }
                },
                videoUploader: {
                    chooseFile: 'اختر فيديو',
                    uploadFile: 'رفع فيديو',
                    uploading: 'جارٍ الرفع...',
                    preview: 'معاينة',
                },
                youTubeVideoSelector: {
                    title: 'اختر فيديو يوتيوب',
                    browseChannel: 'تصفح القناة',
                    pasteLink: 'لصق رابط',
                    pasteLinkDescription: 'الصق رابط فيديو يوتيوب أو معرّف الفيديو لاختياره مباشرة. يعمل أيضاً مع الفيديوهات غير المدرجة',
                    fetch: 'بحث',
                    views: 'مشاهدات',
                    duration: 'المدة',
                    previous: 'السابق',
                    next: 'التالي',
                    page: 'الصفحة',
                    refreshVideos: 'تحديث الفيديوهات',
                    urlPlaceholder: 'https://youtube.com/watch?v=... أو معرّف الفيديو',
                    errors: {
                        noVideosFound: 'لا توجد فيديوهات',
                        invalidUrl: 'رابط يوتيوب أو معرّف فيديو غير صالح',
                        videoNotFound: 'لم يتم العثور على الفيديو',
                    }
                },
                questionsBank: {
                    tableTitle: 'بنوك الأسئلة',
                    questionsBankDescription: 'إدارة مستودعات الأسئلة وبنوك المعرفة',
                    addQuestionsBankDescription: 'إنشاء مستودع بنك أسئلة جديد',
                    editQuestionsBankDescription: 'تحديث بيانات بنك الأسئلة',
                    title: 'العنوان',
                    description: 'الوصف',
                    subjectTitle: 'المادة',
                    chapterTitle: 'الدرس',
                    isActive: 'نشط حاليًا؟',
                    addQuestionsBank: 'إضافة بنك أسئلة',
                    editQuestionsBank: 'تعديل بنك أسئلة',
                    viewQuestionsBank: 'عرض بنك الأسئلة',
                    viewQuestionsBankDescription: 'استعراض أسئلة هذا البنك',
                    deleteQuestionsBank: 'حذف بنك أسئلة',
                    deleteQuestionsBankConfirmTitle: 'تأكيد حذف بنك أسئلة',
                    deleteQuestionsBankConfirmDescription: 'هل أنت متأكد من أنك تريد حذف بنك أسئلة "{{title}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    subject: 'المادة',
                    chapter: 'الدرس',
                    questionsCount: 'عدد الأسئلة',
                    errors: {
                        titleRequired: 'العنوان مطلوب',
                        titleMaxLength: 'يجب ألا يتجاوز العنوان {{max}} حرفًا',
                        descriptionRequired: 'الوصف مطلوب',
                        descriptionMaxLength: 'يجب ألا يتجاوز الوصف {{max}} حرفًا',
                        subjectRequired: 'المادة مطلوبة',
                        noQuestionsBanksFound: 'لا توجد بنوك أسئلة',
                    }
                },
                license: {
                    tableTitle: 'التراخيص',
                    gridDescription: 'إدارة والتحكم في تراخيص الوصول للطلاب',
                    grantLicenseDescription: 'منح تراخيص الوصول للموارد للطلاب',
                    resourceType: 'نوع المورد',
                    resourceTitle: 'عنوان المورد',
                    totalSubscriptionFee: 'إجمالي رسوم الاشتراك',
                    totalFee: 'إجمالي الرسوم',
                    studentCount: 'عدد الطلاب',
                    mixedStatus: 'متفاوتة',
                    startDate: 'تاريخ البدء',
                    endDate: 'تاريخ الانتهاء',
                    attemptsCount: 'عدد المحاولات',
                    status: 'حالة الترخيص',
                    createdAt: 'تاريخ الإنشاء',
                    grantLicense: 'منح ترخيص',
                    deleteLicense: 'حذف الترخيص',
                    deleteLicenseConfirmTitle: 'تأكيد حذف الترخيص',
                    deleteLicenseConfirmDescription: 'هل أنت متأكد من أنك تريد حذف ترخيص المورد "{{title}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    selectStudents: 'حدد الطلاب',
                    accessMonthsCount: 'عدد أشهر الترخيص',
                    chooseGrade: 'اختر الصف',
                    currency: 'العملة',
                    changeLicenseStatusConfirmTitle: 'تأكيد تغيير حالة الترخيص',
                    changeLicenseStatusConfirmDescription: 'هل أنت متأكد من أنك تريد تغيير حالة الترخيص إلى "{{status}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    changeLicenseStatusToPausedOrActive: 'إيقاف / تفعيل الترخيص',
                    changeLicenseStatusToRevoked: 'إلغاء الترخيص',
                    viewStudents: 'عرض الطلاب',
                    licenseStudents: 'طلاب الترخيص',
                    revokeLicense: 'إلغاء الترخيص',
                    revokeLicenseConfirmTitle: 'تأكيد إلغاء ترخيص طالب',
                    revokeLicenseConfirmDescription: 'هل أنت متأكد من أنك تريد إلغاء ترخيص الطالب "{{name}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    resourceTypeLabels: {
                        course: 'دورة',
                        exam: 'امتحان',
                    },
                    licenseStatusLabels: {
                        active: 'نشط',
                        paused: 'موقوف',
                        expired: 'انتهت',
                        revoked: 'تم إلغاؤها',
                    },
                    errors: {
                        licenseStudentsRequired: 'يجب اختيار طالب واحد على الأقل لهذا الترخيص',
                        resourceTypeAllowedValues: 'نوع المورد ينبغي أن يكون من القيم السموح بها',
                        accessMonthsCountPositive: 'عدد أشهر الوصول ينبغي أن يكون أكبر من الصفر',
                        attemptsCountPositive: 'عدد المحاولات ينبغي أن يكون أكبر من الصفر',
                        licenseStatusAllowedValues: 'حالة الترخيص ينبغي أن تكون من القيم السموح بها',
                        noLicensesFound: 'لا توجد تراخيص',
                        resourceRequired: 'المورد مطلوب',
                        noStudentsFound: 'لا يوجد طلاب'
                    }
                },
                user: {
                    tableTitle: 'الموظفين',
                    usersGridDescription: 'إدارة حسابات الموظفين والوظائف وأذونات الوصول',
                    addUserDescription: 'إنشاء حساب موظف جديد',
                    editUserDescription: 'تحديث بيانات الموظف',
                    changeOtherPasswordDescription: 'تغيير كلمة مرور حساب موظف آخر',
                    addUser: 'إضافة موظف',
                    editUser: 'تعديل الموظف',
                    deleteUser: 'حذف الموظف',
                    firstName: 'الاسم الأول',
                    lastName: 'الاسم الأخير',
                    email: 'البريد الإلكتروني',
                    password: 'كلمة المرور',
                    phoneNumber: 'رقم الهاتف',
                    createdAt: 'تاريخ الإنشاء',
                    isActive: 'نشط',
                    roles: 'الوظائف',
                    withRole: 'الوظيفة',
                    deleteUserConfirmTitle: 'تأكيد حذف الموظف',
                    deleteUserConfirmDescription: 'هل أنت متأكد من أنك تريد حذف الموظف "{{name}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    editProfile: 'تعديل الملف الشخصي',
                    profileDescription: 'إدارة معلوماتك الشخصية وأمان حسابك',
                    profileData: 'بيانات الملف الشخصي',
                    changePasswordDescription: 'تحديث كلمة مرور حسابك بأمان',
                    changePassword: 'تغيير كلمة المرور',
                    currentPassword: 'كلمة المرور الحالية',
                    newPassword: 'كلمة المرور الجديدة',
                    phoneNumberHelperText: 'مثال: 01512345678',
                    emailImportantNote: 'لا يمكن تغييره بمجرد التسجيل',
                    changeOtherPassword: 'تغيير كلمة مرور الغير',
                    confirmingPassword: 'تأكيد كلمة المرور',
                    errors: {
                        firstNameRequired: 'الاسم الأول مطلوب',
                        firstNameMaxLength: 'الاسم الأول يجب أن يحتوي على الأكثر {{max}} حرفًا',
                        lastNameRequired: 'الاسم الأخير مطلوب',
                        lastNameMaxLength: 'الاسم الأخير يجب أن يحتوي على الأكثر {{max}} حرفًا',
                        emailRequired: 'البريد الإلكتروني مطلوب',
                        invalidEmail: 'البريد الإلكتروني غير صحيح',
                        passwordRequired: 'كلمة المرور مطلوبة',
                        confirmingPasswordRequired: 'تأكيد كلمة المرور مطلوب',
                        invalidPassword: 'كلمة المرور يجب أن تحتوي على الأقل 8 حرفًا وحرف كبير وحرف صغير ورقم وحرف خاص',
                        invalidConfirmingPassword: 'تأكيد كلمة المرور يجب أن تحتوي على الأقل 8 حرفًا وحرف كبير وحرف صغير ورقم وحرف خاص',
                        phoneNumberRequired: 'رقم الهاتف مطلوب',
                        invalidEgyptianPhoneNumber: 'رقم الهاتف غير صحيح',
                        invalidGrade: 'الصف يجب أن يكون من القيم المسموح بها',
                        noRolesAvailable: 'لا توجد وظائف',
                        rolesNotEmpty: 'يجب أن يحتوي على الأقل وظيفة',
                        noUsersFound: 'لا يوجد موظفين',
                        passwordsDoNotMatch: 'كلمة المرور الجديدة وكلمة المرور التأكيدية لا تتطابقان',
                    }
                },
                role: {
                    tableTitle: 'الوظائف',
                    rolesGridDescription: 'إدارة الوظائف والأذونات وسياسات التحكم بالوصول',
                    addRoleDescription: 'تعريف وظيفة جديدة مع الأذونات',
                    editRoleDescription: 'تعديل بيانات الوظيفة والأذونات',
                    name: 'الاسم',
                    isActive: 'نشط',
                    createdAt: 'تاريخ الإنشاء',
                    addRole: 'إضافة وظيفة',
                    noRolesFound: 'لا توجد وظائف',
                    editRole: 'تعديل وظيفة',
                    deleteRole: 'حذف وظيفة',
                    deleteRoleConfirmTitle: 'تأكيد حذف الوظيفة',
                    deleteRoleConfirmDescription: 'هل أنت متأكد من أنك تريد حذف الوظيفة "{{name}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    claimValues: 'التصريحات',
                    roleLabels: {
                        administrator: 'مدير',
                        student: 'طالب',
                    },
                    claims: {
                        accessSubjectsTab: 'الوصول إلى المواد',
                        accessCoursesTab: 'الوصول إلى الدورات',
                        accessQuestionsTab: 'الوصول إلى الأسئلة',
                        accessQuestionsBanksTab: 'الوصول إلى بنوك الأسئلة',
                        accessExamsTab: 'الوصول إلى الامتحانات',
                        accessPricingPlansTab: 'الوصول إلى خطط التسعير',
                        accessLicensesTab: 'الوصول إلى التراخيص',
                        accessUsersTab: 'الوصول إلى الموظفين',
                        accessRolesTab: 'الوصول إلى الوظائف',
                        accessStudentsTab: 'الوصول إلى الطلاب',
                        accessStudentResultsTab: 'الوصول إلى نتائج الطلاب',
                        accessLiveRoomsTab: 'الوصول إلى الغرف الأونلاين',
                        accessAttendanceTab: 'الوصول إلى تبويب الحضور والغياب',
                        accessFinancialReportsTab: 'الوصول إلى تبويب التقارير المالية',
                        accessEnrollmentRequestsTab: 'الوصول إلى تبويب طلبات التسجيل',
                        accessTenantSettingsTab: 'الوصول إلى تبويب إعدادات المنصة',
                        sendParentNotifications: 'إرسال إشعارات لأولياء الأمور (واتساب)',
                    },
                    errors: {
                        nameRequired: 'الاسم مطلوب',
                        nameMaxLength: 'يجب ألا يتجاوز الاسم {{max}} حرفًا',
                        claimsNotEmpty: 'التصريحات يجب أن تكون محددة',
                    }
                },
                student: {
                    tableTitle: 'الطلاب',
                    studentsGridDescription: 'إدارة حسابات الطلاب والصفوف والتقدم الأكاديمي',
                    addStudentDescription: 'تسجيل حساب طالب جديد',
                    editStudentDescription: 'تحديث بيانات الطالب',
                    changePasswordDescription: 'تغيير كلمة مرور حساب الطالب',
                    firstName: 'الاسم الأول',
                    lastName: 'الاسم الأخير',
                    email: 'البريد الإلكتروني',
                    password: 'كلمة المرور',
                    whatsAppNumber: 'رقم الواتساب',
                    parentPhoneNumber: 'رقم واتساب ولي الأمر',
                    grade: 'الصف',
                    createdAt: 'تاريخ الإنشاء',
                    isActive: 'نشط',
                    addStudent: 'إضافة طالب',
                    editStudent: 'تعديل الطالب',
                    deleteStudent: 'حذف الطالب',
                    changePassword: 'تغيير كلمة المرور',
                    showId: 'عرض البطاقة التعريفية',
                    viewAttendanceHistory: 'سجل الحضور',
                    deleteStudentConfirmTitle: 'تأكيد حذف الطالب',
                    deleteStudentConfirmDescription: 'هل أنت متأكد من أنك تريد حذف الطالب "{{name}}"؟ لا يمكن التراجع عن هذا الإجراء',
                    myProfile: {
                        pageTitle: 'ملفي الشخصي',
                        pageDescription: 'عرض بياناتك الشخصية وتحديثها',
                        formTitle: 'بيانات الملف الشخصي',
                        gradeReadOnlyHint: 'تواصل مع الإدارة لتغيير الصف الدراسي',
                    },
                    errors: {
                        noStudentsFound: 'لا يوجد طلاب',
                    }
                },
                attendance: {
                    pageTitle: 'الحضور والغياب',
                    pageDescription: 'سجّل الحضور بمسح البطاقات التعريفية للطلاب أو يدويًا',
                    manualTab: 'يدوي',
                    scanTab: 'مسح',
                    exportTab: 'تصدير التقرير',
                    searchByEmail: 'البريد الإلكتروني للطالب',
                    searchByEmailPlaceholder: 'أدخل البريد الإلكتروني للطالب',
                    search: 'بحث',
                    markPresent: 'حاضر',
                    markAbsent: 'غائب',
                    academicYear: 'العام الدراسي',
                    export: 'تصدير التقرير',
                    exportDate: 'التاريخ (اختياري)',
                    scanInstruction: 'وجّه الكاميرا نحو رمز QR أو الباركود الخاص بالطالب',
                    scanLaptopCameraHint: 'هل تستخدم كاميرا اللابتوب المدمجة؟ قد تواجه صعوبة في التركيز على مسافة قريبة — جرّب إبعاد الرمز قليلًا، أو استخدم كاميرا خارجية أو كاميرا الهاتف للحصول على أفضل النتائج',
                    cameraError: 'تعذّر الوصول إلى الكاميرا',
                    cameraPermissionDenied: 'تم رفض إذن الوصول إلى الكاميرا',
                    status: {
                        present: 'حاضر',
                        absent: 'غائب',
                    },
                    checkInMethod: {
                        manual: 'يدوي',
                        qrCode: 'رمز QR',
                        barcode: 'باركود',
                    },
                    studentIdDialog: {
                        title: 'البطاقة التعريفية للطالب',
                        description: 'اعرض هذا الرمز لتسجيل الحضور',
                        qrTab: 'رمز QR',
                        barcodeTab: 'باركود',
                        download: 'تنزيل',
                    },
                    history: {
                        title: 'سجل الحضور',
                        noHistory: 'لا توجد سجلات حضور بعد',
                        session: 'الجلسة',
                        date: 'التاريخ',
                        status: 'الحالة',
                        method: 'الطريقة',
                        time: 'الوقت',
                    },
                },
                financialReports: {
                    pageTitle: 'التقارير المالية',
                    pageDescription: 'رسوم مسجّلة ومحدّدة كمدفوعة من قِبل المشرفين — وليست معاملات دفع مؤكدة عبر بوابة دفع، حيث لا تمتلك هذه المنصة بوابة دفع حتى الآن',
                    summary: {
                        licenseFees: 'رسوم التراخيص المسجّلة',
                        liveRoomFees: 'رسوم الغرف الأونلاين المسجّلة',
                        paid: 'مدفوعة',
                        unpaid: 'غير مدفوعة',
                        thisMonth: 'هذا الشهر',
                        thisYear: 'هذا العام',
                        untilNow: 'حتى الآن',
                    },
                    table: {
                        title: 'الرسوم',
                        date: 'التاريخ',
                        type: 'النوع',
                        resource: 'المورد',
                        student: 'الطالب',
                        amount: 'المبلغ',
                        status: 'الحالة',
                        noChargesFound: 'لا توجد رسوم',
                        noChargesHint: 'ستظهر الرسوم هنا عند منح التراخيص أو احتساب رسوم جلسات البث المباشر. جرّب توسيع نطاق التاريخ أو إزالة عوامل التصفية إذا وجدت.',
                    },
                    filters: {
                        startDate: 'من',
                        endDate: 'إلى',
                        chargeType: 'النوع',
                        paidStatus: 'حالة الدفع',
                    },
                    chargeTypeLabels: {
                        license: 'ترخيص',
                        liveRoomSession: 'جلسة غرفة أونلاين',
                    },
                    paidStatusLabels: {
                        paid: 'مدفوعة',
                        unpaid: 'غير مدفوعة',
                    },
                },
                tenantSettings: {
                    branding: {
                        pageTitle: 'الهوية البصرية',
                        pageDescription: 'إدارة اسم منصتك، الوصف، الشعار، والألوان',
                        nameAr: 'الاسم (بالعربية)',
                        nameEn: 'الاسم (بالإنجليزية)',
                        subdomain: 'النطاق الفرعي',
                        subdomainCannotChange: 'ثابت بعد التسجيل ولا يمكن تغييره',
                        description: 'الوصف',
                        logoUrl: 'رابط الشعار',
                        primaryColor: 'اللون الأساسي',
                        secondaryColor: 'اللون الثانوي',
                    },
                    integrations: {
                        pageTitle: 'التكاملات',
                        pageDescription: 'اربط قناتك على يوتيوب وروابط التواصل الاجتماعي',
                        youtubeSettings: 'إعدادات يوتيوب',
                        apiKey: 'مفتاح API الخاص بيوتيوب',
                        apiKeyHelper: 'اختياري — يتجاوز مفتاح API الافتراضي للمنصة',
                        channelId: 'معرّف قناة يوتيوب',
                        channelIdHelper: 'يُستخدم لعرض فيديوهات قناتك كمحتوى للدورات',
                        socialLinks: 'روابط التواصل الاجتماعي',
                        facebook: 'فيسبوك',
                        instagram: 'إنستغرام',
                        x: 'إكس (تويتر)',
                        socialYoutube: 'رابط قناة يوتيوب',
                        whatsAppNotifications: 'إشعارات واتساب',
                        whatsAppNotificationsHint: 'اختر التقارير التي تُرسل تلقائيًا لأولياء الأمور. يمكن إرسال أي تقرير يدويًا بغض النظر عن هذه الإعدادات.',
                        autoSendExamResults: 'إرسال نتائج الاختبارات تلقائيًا',
                        autoSendCourseProgress: 'إرسال تقدم الدورة تلقائيًا',
                        autoSendAttendance: 'إرسال ملخصات الحضور تلقائيًا',
                    },
                    paymentChannels: {
                        pageTitle: 'وسائل الدفع',
                        pageDescription: 'أخبر الطلاب كيف يدفعون لك مباشرةً — بدون بوابة دفع، وبدون أي عمولة تأخذها المنصة',
                        intro: 'اذكر كل وسيلة تقبل بها الدفع (إنستاباي، فودافون كاش، تحويل بنكي، فوري، نقدًا، أو أي وسيلة أخرى). سيرى الطلاب هذه القائمة، مع المبلغ المطلوب بالتحديد، قبل الدفع لك ورفع إيصالهم.',
                        empty: 'لم تتم إضافة أي وسيلة دفع بعد. أضف وسيلة واحدة على الأقل ليعرف الطلاب كيفية الدفع لك.',
                        type: 'نوع الوسيلة',
                        label: 'الاسم المعروض',
                        identifier: 'بيانات الدفع',
                        identifierPlaceholder: 'مثال: 01012345678، أو رقم الحساب / الآيبان',
                        identifierHelper: 'رقم الهاتف أو رقم الحساب أو الكود الذي سيرسل إليه الطلاب الأموال',
                        instructions: 'تعليمات إضافية (اختياري)',
                        instructionsPlaceholder: 'مثال: "يرجى كتابة اسمك في ملاحظة التحويل"',
                        addChannel: 'إضافة وسيلة دفع',
                        removeChannel: 'إزالة',
                        newChannel: 'وسيلة جديدة',
                        deleteConfirmTitle: 'إزالة وسيلة الدفع',
                        deleteConfirmDescription: 'هل أنت متأكد من إزالة "{{label}}"؟ لن يراها الطلاب بعد الآن كوسيلة للدفع لك.',
                        types: {
                            InstaPay: 'إنستاباي',
                            VodafoneCash: 'فودافون كاش',
                            OrangeCash: 'أورانج كاش',
                            EtisalatCash: 'اتصالات كاش',
                            BankTransfer: 'تحويل بنكي',
                            Fawry: 'فوري',
                            Cash: 'نقدًا',
                            Other: 'أخرى',
                        },
                        errors: {
                            typeRequired: 'نوع الوسيلة مطلوب',
                            labelRequired: 'الاسم المعروض مطلوب',
                            identifierRequired: 'بيانات الدفع مطلوبة',
                        },
                    },
                    errors: {
                        nameArRequired: 'الاسم بالعربية مطلوب',
                        nameArMaxLength: 'يجب ألا يتجاوز الاسم بالعربية {{max}} حرفًا',
                        nameEnRequired: 'الاسم بالإنجليزية مطلوب',
                        nameEnMaxLength: 'يجب ألا يتجاوز الاسم بالإنجليزية {{max}} حرفًا',
                        descriptionMaxLength: 'يجب ألا يتجاوز الوصف {{max}} حرفًا',
                        logoUrlMaxLength: 'يجب ألا يتجاوز رابط الشعار {{max}} حرفًا',
                        invalidHexColor: 'يجب أن يكون لونًا سداسيًا صحيحًا (مثال: #2563EB)',
                    },
                },
                liveRoom: {
                    pageTitle: 'الغرف الأونلاين',
                    pageDescription: 'أنشئ الغرف، عيّن الطلاب، وتحكم في الانضمام',
                    tableTitle: 'الغرف الأونلاين',
                    createNew: 'إنشاء غرفة',
                    columns: {
                        title: 'العنوان',
                        grade: 'الصف',
                        assignedStudents: 'الطلاب المعيّنون',
                        attendees: 'حضور اليوم',
                        opened: 'مفتوحة',
                    },
                    openedLabels: {
                        opened: 'مفتوحة',
                        closed: 'مغلقة',
                    },
                    actions: {
                        edit: 'تعديل',
                        delete: 'حذف',
                        join: 'الانضمام',
                        manageStudentAssignments: 'تعيين / إلغاء تعيين الطلاب',
                        viewAttendance: 'عرض الحضور',
                    },
                    fields: {
                        title: 'العنوان',
                        description: 'الوصف',
                        grade: 'الصف',
                        opened: 'مفتوحة',
                        costPerSession: 'تكلفة الجلسة',
                        costPerSessionHelper: 'تكلفة الجلسة، مع اعتبار أن كل طالب يأخذ جلسة واحدة في اليوم، بغض النظر عن وقت انضمامه. 0 = مجانية',
                        currency: 'العملة',
                    },
                    errors: {
                        titleRequired: 'العنوان مطلوب',
                        titleMaxLength: 'يجب ألا يتجاوز العنوان {{max}} حرفًا',
                        descriptionRequired: 'الوصف مطلوب',
                        descriptionMaxLength: 'يجب ألا يتجاوز الوصف {{max}} حرفًا',
                        gradeRequired: 'الصف مطلوب',
                        costPerSessionInvalid: 'يجب أن تكون تكلفة الجلسة 0 أو أكثر',
                        currencyRequired: 'العملة مطلوبة',
                        noRoomsFound: 'لا توجد غرف أونلاين',
                        noStudentsFound: 'لا يوجد طلاب',
                        noAttendance: 'لم يتم تسجيل أي حضور بعد',
                        accessTokenError: 'فشل الحصول على رمز الوصول. يرجى المحاولة مرة أخرى',
                    },
                    dialog: {
                        createTitle: 'إنشاء غرفة أونلاين',
                        editTitle: 'تعديل الغرفة الأونلاين',
                        createButton: 'إنشاء',
                        deleteConfirmTitle: 'حذف الغرفة الأونلاين',
                        deleteConfirmDescription: 'هل أنت متأكد من حذف غرفة "{{title}}"؟ لا يمكن التراجع عن هذا الإجراء',
                        attendanceTitle: 'كشف الحضور',
                        manageStudentAssignmentsTitle: 'تعيين / إلغاء تعيين الطلاب',
                        manageStudentAssignmentsDescription: 'يتم عرض طلاب الصف الخاص بالغرفة فقط. يتم الاحتفاظ بالاختيار عبر الصفحات',
                        searchStudents: 'ابحث بالاسم الأول…',
                        assignedSelectedCount: 'تم اختيار {{count}} طالب',
                    },
                    attendance: {
                        studentName: 'اسم الطالب',
                        email: 'البريد الإلكتروني',
                        firstJoinedAt: 'أول انضمام',
                        lastLeftAt: 'آخر مغادرة',
                        duration: 'إجمالي المدة',
                        cost: 'التكلفة',
                        paid: 'مدفوع',
                        markPaid: 'تحديد كمدفوع',
                        markUnpaid: 'تحديد كغير مدفوع',
                        filterDate: 'التاريخ',
                    },
                    room: {
                        hostHeader: 'أنت تستضيف هذه الغرفة',
                        participantHeader: 'الغرفة الأونلاين قيد التشغيل',
                        leave: 'مغادرة',
                        controls: {
                            mic: 'الميكروفون',
                            camera: 'الكاميرا',
                            screenShare: 'مشاركة الشاشة',
                            muteMic: 'كتم الميكروفون',
                            unmuteMic: 'إلغاء كتم الميكروفون',
                            startCamera: 'تشغيل الكاميرا',
                            stopCamera: 'إيقاف الكاميرا',
                            shareScreen: 'مشاركة الشاشة',
                            stopShare: 'إيقاف المشاركة',
                            fullscreen: 'ملء الشاشة',
                            exitFullscreen: 'الخروج من ملء الشاشة',
                        },
                    },
                    student: {
                        pageTitle: 'غرفي الأونلاين',
                        pageDescription: 'الغرف التي تم تعيينك إليها',
                        emptyAssigned: 'لم يتم تعيينك إلى أي غرفة بعد',
                        joinNow: 'انضمام الآن',
                        inactiveLabel: 'مغلقة — لا يمكن الانضمام الآن',
                    },
                },
                studentResults: {
                    pageTitle: 'نتائج الطلاب',
                    pageDescription: 'مراجعة أداء الطلاب في الامتحانات والدورات',
                    searchPlaceholder: 'ابحث بالاسم أو البريد الإلكتروني…',
                    tabs: {
                        all: 'الكل',
                        secondaryFirst: 'الصف الأول الثانوي (أو ما يعادله)',
                        secondarySecond: 'الصف الثاني الثانوي (أو ما يعادله)',
                        secondaryThird: 'الصف الثالث الثانوي (أو ما يعادله)',
                    },
                    columns: {
                        name: 'الاسم',
                        email: 'البريد الإلكتروني',
                        grade: 'الصف',
                        examsPassedTotal: 'الامتحانات (الناجحة/الإجمالي)',
                        avgExamScore: 'متوسط درجة الامتحان %',
                        coursesProgress: 'عناصر الدورات (المنجزة/الإجمالي)',
                        lastActive: 'آخر نشاط',
                    },
                    cells: {
                        passedOfTotal: '{{passed}}/{{total}}',
                        finishedOfTotal: '{{finished}}/{{total}}',
                    },
                    actions: {
                        viewDetails: 'عرض التفاصيل',
                    },
                    errors: {
                        noStudentsFound: 'لا يوجد طلاب',
                    },
                    detail: {
                        title: 'تفاصيل نتائج الطالب',
                        enrolledAt: 'تاريخ التسجيل',
                        close: 'إغلاق',
                        sendReportToParent: 'إرسال تقرير لولي الأمر (واتساب)',
                        noParentContact: 'لا يوجد بيانات تواصل لولي أمر هذا الطالب',
                        loadingError: 'فشل تحميل تفاصيل الطالب. يرجى المحاولة مرة أخرى',
                        tabs: {
                            exams: 'الامتحانات',
                            courses: 'الدورات',
                        },
                        exams: {
                            attemptNumber: 'المحاولة رقم {{number}}',
                            score: '{{earned}}/{{max}} ({{percent}}٪)',
                            scoreLabel: 'الدرجة',
                            passed: 'ناجح',
                            failed: 'راسب',
                            inProgress: 'قيد التنفيذ',
                            started: 'بدأ في: {{date}}',
                            completed: 'انتهى في: {{date}}',
                            noAttempts: 'لا توجد محاولات امتحان بعد',
                            question: 'س{{number}}',
                            yourAnswer: 'إجابتك',
                            scoreOutOf: '{{earned}}/{{max}}',
                            mentalMathBlank: 'فارغ',
                            mentalMathCorrectAnswer: 'الإجابة الصحيحة',
                        },
                        courses: {
                            noCourses: 'لا يوجد تقدم في الدورات بعد',
                            progress: '{{finished}}/{{total}} عنصر',
                        },
                    },
                },
                notifications: {
                    title: 'الإشعارات',
                    empty: 'لا توجد إشعارات بعد',
                    markAllRead: 'تحديد الكل كمقروء',
                    typeLabels: {
                        licenseGranted: 'تم منح رخصة',
                        licenseExpiringSoon: 'رخصة على وشك الانتهاء',
                        courseContentPublished: 'تمت إضافة محتوى جديد',
                        attendanceMarkedAbsent: 'تم تسجيلك غائبًا',
                    },
                },
                common: {
                    switchLanguage: 'تبديل اللغة',
                    lightMode: 'الوضع الفاتح',
                    darkMode: 'الوضع الداكن',
                    or: 'أو',
                    displaySettings: 'إعدادات العرض',
                    appearance: 'المظهر',
                    appVersion: 'الإصدار {{version}}',
                    loading: 'جاري التحميل ...',
                    backToMain: 'العودة إلى الصفحة الرئيسية',
                    cancel: 'إلغاء',
                    confirm: 'تأكيد',
                    save: 'حفظ',
                    retry: 'إعادة المحاولة',
                    submit: 'تأكيد',
                    submitting: 'جاري التأكيد ...',
                    delete: 'حذف',
                    active: 'نشط',
                    inactive: 'غير نشط',
                    actions: 'الإجراءات',
                    filterPanel: 'شريط البحث',
                    applyFilters: 'تطبيق الفلاتر',
                    clearFilters: 'إلغاء الفلاتر',
                    all: 'الكل',
                    none: 'لا شيء',
                    optional: '(اختياري)',
                    yes: 'نعم',
                    no: 'لا',
                    later: 'لاحقاً',
                    updateNow: 'تحديث الآن',
                    copy: 'نسخ',
                    close: 'إغلاق',
                    admin: 'مدير النظام',
                    student: 'طالب',
                    user: 'موظف',
                    pagination: {
                        rowsPerPage: 'عدد الصفوف لكل صفحة',
                        displayedRows: 'الصفوف {{from}}-{{to}} من {{count}}'
                    },
                    notFound: {
                        title: 'الصفحة غير موجودة',
                        description: 'عذراً، يبدو أن الصفحة التي تبحث عنها غير موجودة',
                    },
                    unauthorized: {
                        title: 'غير مصرح لك',
                        description: "عذرًا، يبدو أن الصفحة التي تبحث عنها غير مصرح لك بالدخول إليها.",
                    },
                    errors: {
                        networkError: 'حدث خطأ أثناء الاتصال بالخادم',
                    },
                },
                ai: {
                    chat: {
                        title: 'المساعد الذكي',
                        subtitle: 'إصدار تجريبي — متاح للمدرسين',
                        openTooltip: 'المساعد الذكي',
                        inputPlaceholder: 'اكتب سؤالك للمساعد…',
                        reset: 'بدء محادثة جديدة',
                        emptyState: 'كيف يمكنني مساعدتك اليوم؟',
                        emptyHint: 'جرّب: "ما المواد المتاحة لدينا؟" أو "اعرض امتحانات الصف الثالث الثانوي"',
                        capabilitiesTitle: 'ما يمكنني مساعدتك به',
                        errors: {
                            unknown: 'حدث خطأ غير متوقع لدى المساعد',
                        },
                    },
                    generate: {
                        openButton: 'توليد الأسئلة بالذكاء الاصطناعي',
                        title: 'توليد الأسئلة',
                        targetBankNotice: 'ستُحفظ الاقتراحات في بنك الأسئلة: {{bankTitle}}',
                        sourceText: 'المادة العلمية',
                        sourceHelp: 'الصق درسًا أو ملاحظات أو فقرة، وسيقترح المساعد أسئلة منها',
                        numberOfQuestions: 'عدد الأسئلة',
                        preferredType: 'نوع السؤال المفضّل',
                        preferredDifficulty: 'مستوى الصعوبة المفضّل',
                        mixed: 'متنوّع',
                        notes: 'تعليمات للمساعد (اختياري)',
                        notesHelp: 'مثال: "ركّز على البناء الضوئي"، أو "تجنّب الأسئلة المراوِغة"',
                        generate: 'توليد',
                        generating: 'جارٍ التوليد…',
                        suggestionsHeading: '{{count}} اقتراح جاهز للمراجعة',
                        saveSelected: 'حفظ {{count}} سؤالاً في البنك',
                        saving: 'جارٍ الحفظ…',
                        close: 'إغلاق',
                        correct: 'الإجابة الصحيحة',
                        savedCount: 'تم حفظ {{count}} سؤالاً بنجاح',
                        errors: {
                            sourceRequired: 'المادة العلمية مطلوبة',
                            generationFailed: 'تعذّر توليد الأسئلة، حاول تقليل النص أو إعادة صياغته',
                            partialSaveFailure: 'تعذّر حفظ {{count}} سؤال',
                        },
                    },
                },
            }
        }
    },
    lng: localStorage.getItem('language') || LanguageCode.AR,
    fallbackLng: LanguageCode.AR,
    interpolation: {
        escapeValue: false,
        // Any numeric value interpolated into a translation (counts, scores, percentages, ...)
        // automatically renders in the current language's digits — Arabic-Indic for ar-EG — with
        // zero changes needed at any individual t() call site. Strings (phone numbers, ids kept
        // as strings, etc.) pass through untouched.
        format: (value, _format, lng) => (typeof value === 'number' ? new Intl.NumberFormat(lng).format(value) : value),
    }
});

export default i18n;